import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
  Plus, X, Archive, ArchiveRestore, Trash2, ChevronDown, ChevronUp, MessageSquare, BookMarked, Brain,
  Loader2, Sparkles, CheckCircle2, AlertCircle, Pencil, ExternalLink, Briefcase, Redo2,
} from 'lucide-react';
import InlineChat, { Message, newMessageId } from './InlineChat';
import { PortalBadge } from './arkorbit-ui/PortalBadge';
import { PortalSwitch } from './arkorbit-ui/PortalSwitch';
import { CURSOR_PORTAL, PORTAL_BTN, PORTAL_CHAT, PORTAL_TAB } from './arkorbit-ui/portalTheme';
import { portalArkoSurfaceClass } from './portal/portalAppearance';
import { usePortalAppearance } from './portal/portalAppearanceContext';
import { ArkoCursorThreadSearch } from './portal/ArkoCursorComposer';
import {
  MemoryNudgeDialog,
  MemorySessionDialog,
  MemoryThumbDialog,
  AkteHandoffDialog,
} from './portal/MemoryDialogs';
import {
  type MemcoreContext,
  type MemProofItem,
  type PinnedFact,
  addPinnedFact,
  buildChatTranscript,
  hatSpeicherbarenInhalt,
  makePinnedFact,
  pinnedFactToMemItem,
  removePinnedFact,
} from './portal/memcore';
import {
  buildDemoApprovalFristPayload,
  isDemoFristUrlEnabled,
  shouldAllowDemoFristCard,
} from './portal/verfahrensmotor/demoFristSeed';
import {
  isDocumentAnalyzeDone,
  clearDocumentAnalyzeGuard,
} from './portal/portalDocumentAnalyzeGuard';
import {
  ARKO_GUIDED_FLOW_EVENT,
  ARKO_HUB_MODES_LEGACY,
  arkoHubModeLabel,
  nextAkteHubModeId,
  consumePendingGuidedFlow,
  type ArkoHubIntentDetail,
} from './portal/arkoHubModes';
import { akteOnlyofficePlusKind, portalFileTypeMark } from './portal/portalOnlyofficeSurface';
import {
  guidedFlowTitle,
  isGuidedFlowId,
  startGuidedFlow,
  type GuidedFlowBotBlock,
  type GuidedFlowId,
  type GuidedFlowState,
  EMPTY_GUIDED_FLOW,
} from './portal/arkoGuidedFlows';
import type {
  PortalAkteThreadStand,
  PortalDocumentOpenFields,
  PortalOpenDocumentMeta,
  PortalSelectedDocumentPayload,
} from './portal/portalDocuments';
import {
  aktePromptHasExtractBody,
  akteStandContentEquals,
  appendAkteStandToDocumentContext,
  buildAkteThreadStand,
  formatAkteStandParagraph,
  formatSynthesisDocumentLabels,
  PORTAL_MAX_OPEN_CHATS,
  PORTAL_AKTE_FOLLOWUP_CONTEXT_MARKER,
  PORTAL_LOCAL_OCR_MARKER,
  PORTAL_SYNTHESIS_MAX_SELECTED_DOCS,
  previewAkteStandLabel,
} from './portal/portalDocuments';
import { clearBackgroundJobStorage } from './portal/portalBackgroundJobs';
import { useBindPortalBackgroundLiveThread, usePortalBackgroundJobsContext } from './portal/portalBackgroundJobsContext';
import { startBackgroundDocumentAnalysis as enqueueBackgroundDocumentAnalysis } from './portal/portalDocumentRowAnalysis';
import { ArkoBackgroundAnalysisChip } from './portal/ArkoBackgroundAnalysisChip';
import { preferFullExtractForAnalysis } from './portal/portalBackgroundAnalysisChunks';
import type { PortalBoundAkte } from './portal/portalAktenTypes';
import {
  buildLocalAkteHandoff,
  capAkteHandoff,
  continueAkteChatFields,
  decideBindAkteThread,
  partitionArkoThreads,
  nextAkteContextBindId,
  plusChatUnboundFields,
  resolveDocumentContextEnabled,
  shouldAutoSelectFocusDocument,
  shouldPersistWorkspace,
  workspaceHasMessages,
  workspaceHasUserMessages,
} from './portal/portalArkoThreadBind';
import { arkoThreadVisibleInScope, akteChatActionAllowed, aktePopoversAfterOutsideClick, applyAkteChatAction, newArkoChatAkteId } from './portal/portalAkteRahmen';
import {
  effectivePortalAkteNcPath,
  loadPortalAkteDocsPanelExpanded,
  savePortalAkteDocsPanelExpanded,
} from './portal/portalAkten';
import { PORTAL_AKTE_LAZY_EXTRACT_MARKER, PORTAL_AKTE_LAZY_EXTRACT_RECOGNIZING_HINT, hasUsableAkteExtract, isFailedAkteLazyExtractContext, isPendingAkteLazyExtractContext, isRecognizingAkteLazyExtractContext, akteLazyExtractFailureReason } from './portal/portalAkteLazyExtract';
import {
  AKTE_FUEHREN_CHAT_ACTIONS,
  AKTE_FUEHREN_CHAT_LABELS,
  PORTAL_AKTE_FUEHREN_MARKER,
  PORTAL_AKTE_SUBSUMTION_MARKER,
  PORTAL_AKTE_SCHREIBEN_MARKER,
  PORTAL_AKTE_SCHREIBEN_HINT,
  akteFuehrenChatActionsHint,
  akteFuehrenChatActionsReady,
  buildAkteFuehrenPrompt,
  dispatchAkteFuehrenChat,
  excludeIrrelevantAkteDoc,
  extractAkteSchreibenConcern,
} from './portal/portalAkteFuehren';

const STORAGE_PREFIX = 'arkorbit_portal_arko_threads_v1__';
const MAX_MESSAGES_PER_THREAD = 80;
const SAVE_DEBOUNCE_MS = 300;
/** Desktop: Chats | Akten. Unter ~1180px stapeln — Florian 13″, wie DokumenteView. */
const THREAD_SPLIT_GRID = 'grid grid-cols-1 gap-1.5 min-w-0 @min-[1180px]:grid-cols-2';

interface Thread {
  id: string;
  title: string;
  messages: Message[];
  updatedAt: number;
  /** Pro Thread: Dokument-Kontext an/aus (persistiert). */
  documentContextEnabled?: boolean;
  /** true = Nutzer hat explizit umgeschaltet; View-Default darf nicht überschreiben. */
  documentContextUserOverride?: boolean;
  /** Slice 1.3b: explizite Dokument-Auswahl, fest an diesen ARKO-Thread gebunden. */
  selectedDocumentIds?: string[];
  /** Aktenpfad beim letzten Ändern der Auswahl; reine Bindungsmetadaten, kein Volltext. */
  documentContextAkteNcPath?: string | null;
  /** Aktenraum v1: Chat-Thread an portal_akten.id — Wechsel lädt den passenden Verlauf. */
  akteId?: string | null;
  /**
   * Aktenraum v2: Kurz-Stand (gewählte Doc-Titel + letzte Orientierung).
   * Lebt am Thread mit akteId im bestehenden localStorage — kein PB, kein Klartext-Log.
   */
  aktenStand?: PortalAkteThreadStand;
  /** MEMCORE Modul B: Speichern-Nudge wurde für diesen Thread mind. 1× beantwortet (§7.7). */
  memNudgeDone?: boolean;
  /**
   * Stream 4: Vom Anwalt gepinnte Fall-Eckdaten (Mandant, Akte, Gegner). Hängen
   * bei jeder Anfrage dieses Threads automatisch als memContext am Body — spätere
   * Entwürfe werden vorbefüllt, ohne dass ARKO erneut nachfragt. Client-seitig
   * (localStorage, 1 Sitz = 1 Thread); roher PII-Text bleibt im Browser.
   */
  caseFacts?: PinnedFact[];
  /** Phase 1.3: Geführter Kachel-Flow (persistiert pro Thread). */
  guidedFlow?: GuidedFlowState;
  /** Gewählter Composer-Modus dieser Chats (Id aus ARKO_HUB_MODES_LEGACY). */
  hubModeId?: string | null;
  /** Sichtbarer Arbeitsstand nach Weiterarbeiten — geht als Kontextzeile ins LLM. */
  akteHandoff?: string | null;
  /** Nach Handoff archiviert — decideBindAkteThread darf ihn nicht wieder aktivieren. */
  handedOff?: boolean;
}

/** MEMCORE Modul B (Labor): aktiviert Session-Speichern, Nudge + Daumen-Dialog. */
export interface ArkoMemcoreProps extends MemcoreContext {
  curatorUrl: string;
}

interface ArchivedThread extends Thread {
  archivedAt: number;
}

interface WorkspaceState {
  version: 1;
  activeThreadIds: string[];
  activeId: string;
  threads: Record<string, Thread>;
  archived: Record<string, ArchivedThread>;
}

/** Frontend-Abort für Kanzlei-ARKO: n8n/Ollama Labor ~50–56s; 90s Puffer (Donna nutzt 120s). */
export const PORTAL_ARKO_CHAT_TIMEOUT_MS = 90_000;
/** Pro Analyse-Call (ein Extract-Fenster). Folge-POSTs derselben Job-ID, kein 180s-Gesamt-Singleton. */
export const PORTAL_ARKO_BACKGROUND_ANALYSIS_TIMEOUT_MS = 180_000;

interface PortalArkoChatWorkspaceProps {
  customerId: string;
  webhookUrl: string;
  initialBotMessage: string;
  title: string;
  subtitle: string;
  accentColor?: 'blue' | 'purple' | 'amber';
  mode?: string;
  extraBody?: Record<string, unknown>;
  /** Judikatur-Chip nur bei offenem Sitz. */
  judicatureOpen?: boolean;
  /** Hintergrundanalyse nur bei ARKO freigeschaltet. */
  backgroundAnalysisOpen?: boolean;
  /** Default {@link PORTAL_ARKO_CHAT_TIMEOUT_MS} — InlineChat-Fallback 45s gilt nicht im Portal. */
  timeoutMs?: number;
  showQuickActions?: boolean;
  showRechtsfrageQuickAction?: boolean;
  /** Optionaler Dokument-/Editor-Kontext fürs Webhook-Body (n8n); null/undefined = weglassen. */
  documentContext?: string | null;
  /** Anzeigename des geöffneten Dokuments (Toggle-Hinweis). */
  documentDisplayName?: string | null;
  /** View-basierter Default für den Dokument-Kontext-Toggle (Parent setzt nach view). */
  defaultDocumentContextEnabled?: boolean;
  /** Split-View Dokument (Alpha): Pane-Header „ARKO“ reicht — kein zweiter InlineChat-Header. */
  hideChatHeader?: boolean;
  /** Schmale Cursor-artige Toolbar (Tabs + Kontext in 1–2 Zeilen). */
  compactChrome?: boolean;
  /** z. B. `border-0 shadow-none rounded-none` im Dokument-Split. */
  chatShellClassName?: string;
  /** Stream D: Vertragsgenerator-Slot-Filling im Chat aktivieren (Premium + Feature an). */
  enableContractIntent?: boolean;
  /** Ruft den Generate-Endpoint, lädt das Ergebnis in den Editor und liefert die Chat-Antwort. */
  onContractGenerate?: (args: {
    templateType: string;
    fields: Record<string, unknown>;
    displayName: string;
  }) => Promise<{ ok: boolean; message: string }>;
  /** Stream 3: Schreiben-/Schriftsatz-Entwurf als PDF exportieren (Gotenberg). */
  onExportSchreibenPdf?: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  onExportSchreibenDocx?: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  /** Datei-Karte im Chat → Portal-Viewer (OnlyOffice/PDF), kein Nextcloud-Tab. */
  onOpenPortalDocument?: (fields: PortalDocumentOpenFields) => void | Promise<void>;
  /** Slice 1.3b V2: Synthese-Entwurf ins geöffnete oder neues Dokument übernehmen. */
  onApplySynthesisToDocument?: (args: {
    text: string;
    target: 'existing' | 'new';
  }) => Promise<{ ok: boolean; message: string }>;
  /** Slice 1.3b V2: Dokument im Editor → beide Übernahme-Buttons im Chat. */
  synthesisHasOpenDocument?: boolean;
  /** Parent: letzter Chip/Intent für Startseite „Zuletzt“. */
  onActiveIntentChange?: (label: string | null) => void;
  /** MEMCORE Modul B (Labor): null/undefined = Gedächtnis-UI komplett aus. */
  memcore?: ArkoMemcoreProps | null;
  /** `cursor` = Agents-Layout mit Thread-Rail (ARKO-Tab); `compact` = Dokument-Split. */
  layoutVariant?: 'default' | 'cursor' | 'compact';
  /** Nur der ARKO-Reiter. Dokument-Split und Akten bleiben dunkel. */
  followAppearance?: boolean;
  cursorCustomerName?: string;
  cursorKanzlei?: string;
  /** Demo-Smoke `?demoFrist=1` — Mandanten-Pfad für Demo-Card in neuem Thread. */
  customerStatus?: string;
  customerEmail?: string;
  /** Extract-Fingerprint (Dokument + Revision + Kontextlänge) — Guard gegen Re-Analyse. */
  documentExtractFingerprint?: string | null;
  documentExtractRevision?: number;
  quellDokumentNcPath?: string | null;
  quellDokumentPbId?: string | null;
  akteNcPath?: string | null;
  /** Aktenraum v1: gebundene Akte (Dokumente-Reiter und Chat teilen denselben Binding). */
  boundAkte?: PortalBoundAkte | null;
  /**
   * haupt = nur Chats ohne Akte.
   * akte = nur Chats mit genau dieser boundAkte.id.
   */
  chatHome?: 'haupt' | 'akte';
  /** Spalte neben einem Dokument ohne Akte: keine Akten-Steuerung. */
  freeDocumentColumn?: boolean;
  akten?: PortalBoundAkte[];
  akteDocumentCount?: number;
  onBoundAkteChange?: (akte: PortalBoundAkte | null) => void;
  /** Aktenraum v1.1: nach „Akte öffnen“ den Doc-Kasten einmal aufklappen. */
  akteDocsPanelOpenNonce?: number;
  onOpenAkteDocument?: (documentId: string) => void;
  onRenameAkteDocument?: (documentId: string, newName: string) => Promise<void>;
  canWriteAkteDocuments?: boolean;
  /** Slice 1.3: Fokussiertes Dokument (Editor-Tab) — wechselt `documentContext`, kein neuer Chat-Thread. */
  activeDocumentId?: string | null;
  /** Ungekürzter Extract des Fokus-Docs (OO/pdf2text), nicht der Chat-7500-Kopf. */
  activeDocumentFullExtract?: {
    text: string;
    pages?: number;
    rawBytes?: number;
    truncated?: boolean;
    source?: string;
  } | null;
  /** Slice 1.3: Metadaten aller offenen Editor-Tabs für Webhook `openDocuments[]`. */
  openDocumentsMeta?: PortalOpenDocumentMeta[];
  /** Slice 1.3b: Alle offenen Docs samt Cache; der aktive Thread wählt daraus sein Set. */
  availableDocuments?: PortalSelectedDocumentPayload[];
  /** Bindet die Checkboxen der Editor-Tab-Leiste an den aktiven ARKO-Thread. */
  onDocumentSelectionControllerChange?: (
    controller: ((documentId: string) => void) | null,
  ) => void;
  /** Spiegel für die Editor-Tab-Checkboxen; Quelle der Wahrheit bleibt der Thread. */
  onSelectedDocumentIdsChange?: (selectedDocumentIds: string[]) => void;
  /** Aktenraum v2.8: Lazy-Extract läuft für diese Doc-IDs (ohne OO-Tab). */
  extractingDocumentIds?: string[];
  /** Nach Analyse-Upload: Dateien im Hub registrieren und im Editor öffnen. */
  onAnalyzeDocumentsReady?: (fields: import('./portal/portalDocuments').PortalDocumentOpenFields[]) => void | Promise<void>;
  /** Phase 1.3: PB-Token für Akten-Anlage in geführten Flows. */
  authToken?: string | null;
  /** Phase 1.4: Absatz-Index + cursorSelection für Webhook. */
  paragraphIndexMap?: import('./portal/portalParagraphMap').ParagraphIndexEntry[];
  cursorSelection?: import('./portal/portalParagraphMap').CursorSelection | null;
  onMarkDocumentAnalyzed?: () => void;
}

function storageKey(customerId: string): string {
  return `${STORAGE_PREFIX}${customerId}`;
}

/**
 * Loescht alle ARKO-Chats dieses Kunden aus dem Browser.
 * Nur fuer bewusste Voll-Wipe-Pfade — NICHT beim Logout (Verlauf bleibt
 * pro customerId in localStorage ueber Abmelden/Anmelden und Hard-Refresh).
 * Einzelne Threads loescht die UI via archive/delete.
 */
export function clearArkoChatStorage(customerId: string): void {
  try { localStorage.removeItem(storageKey(customerId)); } catch { /* noop */ }
  clearBackgroundJobStorage(customerId);
  clearDocumentAnalyzeGuard(customerId);
}

function newThreadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'thr_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function defaultThreadTitle(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `Chat ${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function hasDocumentContext(ctx: string | null | undefined): boolean {
  return ctx != null && ctx !== '';
}

function deriveTitleFromMessages(messages: Message[], fallback: string): string {
  const firstUser = messages.find(m => m.role === 'user');
  if (firstUser && firstUser.text.trim()) {
    const trimmed = firstUser.text.trim().replace(/\s+/g, ' ');
    return trimmed.length > 40 ? trimmed.slice(0, 40) + '…' : trimmed;
  }
  return fallback;
}

function threadHasDemoFristCard(messages: Message[]): boolean {
  return messages.some(
    (m) => m.messageKind === 'approval_frist' || Boolean(m.approvalFrist),
  );
}

/** Labor `?demoFrist=1`: Demo-Card erst nach User-Nachricht + Bot-Antwort (nicht beim Thread-Start). */
function maybeAppendDemoFristCard(
  messages: Message[],
  customerStatus?: string,
  customerEmail?: string,
  documentContext?: string | null,
): Message[] {
  if (!shouldAllowDemoFristCard(documentContext) || threadHasDemoFristCard(messages)) return messages;

  const userCount = messages.filter((m) => m.role === 'user').length;
  if (userCount < 1) return messages;

  const lastUserIdx = messages.map((m) => m.role).lastIndexOf('user');
  const hasBotReplyAfterUser = messages
    .slice(lastUserIdx + 1)
    .some((m) => m.role === 'bot' && m.messageKind !== 'approval_frist');
  if (!hasBotReplyAfterUser) return messages;

  const payload = buildDemoApprovalFristPayload(customerStatus, customerEmail);
  return [
    ...messages,
    {
      role: 'bot',
      text: 'Ich habe eine Berufungsfrist erkannt (Demo-Smoke). Bitte bestätigen Sie den Vorschlag.',
      messageKind: 'approval_frist',
      approvalFrist: payload,
      id: newMessageId(),
    },
  ];
}

function guidedBlocksToMessages(blocks: GuidedFlowBotBlock[]): Message[] {
  return blocks.map((block) => ({
    role: 'bot' as const,
    text: block.text,
    id: newMessageId(),
    messageKind: 'guided_flow' as const,
    ...(block.chips?.length ? { guidedFlowChips: block.chips } : {}),
    ...(block.riskFindings?.length ? { guidedRiskFindings: block.riskFindings } : {}),
    ...(block.laborDemo ? { guidedLaborDemo: true } : {}),
    ...(block.mandatsConfirm ? { mandatsConfirm: block.mandatsConfirm } : {}),
  }));
}

function makeFreshThread(): Thread {
  return {
    id: newThreadId(),
    title: defaultThreadTitle(),
    messages: [],
    updatedAt: Date.now(),
  };
}

function threadFromArchive(arch: ArchivedThread): Thread {
  const { archivedAt: _archivedAt, ...rest } = arch;
  return { ...rest, updatedAt: Date.now() };
}

const AKTE_STAND_SKIP_BOT_KINDS = new Set([
  'document_focus_switch',
  'document_welcome',
  'approval_frist',
  'guided_flow',
]);

function lastUserTextForStand(messages: Message[]): string {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.role === 'user' && msg.text?.trim()) return msg.text.trim();
  }
  return '';
}

function lastBotTextForStand(messages: Message[]): string {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.role !== 'bot') continue;
    if (msg.messageKind && AKTE_STAND_SKIP_BOT_KINDS.has(msg.messageKind)) continue;
    const text = msg.text?.trim() || '';
    if (!text) continue;
    if (/^ARKO arbeitet jetzt mit:/i.test(text)) continue;
    if (/^Ich beziehe mich ab jetzt/i.test(text)) continue;
    return text;
  }
  return '';
}

function selectedTitlesForStand(
  thread: Thread,
  docsById: Map<string, PortalSelectedDocumentPayload>,
): string[] {
  return (thread.selectedDocumentIds ?? [])
    .map((id) => docsById.get(id)?.title?.trim() || '')
    .filter(Boolean);
}

function nextAkteStandForThread(
  thread: Thread,
  messages: Message[],
  docsById: Map<string, PortalSelectedDocumentPayload>,
  focusTitle?: string | null,
): PortalAkteThreadStand | undefined {
  const akteId = thread.akteId?.trim();
  if (!akteId) return undefined;
  return buildAkteThreadStand({
    selectedDocTitles: selectedTitlesForStand(thread, docsById),
    focusTitle: focusTitle ?? null,
    lastUserText: lastUserTextForStand(messages),
    lastBotText: lastBotTextForStand(messages),
  });
}

function akteThreadTitle(title: string): string {
  const clean = title.trim() || 'Akte';
  return `Akte: ${clean}`;
}

/** Neuer Thread mit geführtem Kachel-Flow (immer dediziert, nie bestehenden Chat wiederverwenden). */
function buildGuidedFlowThreadPatch(
  modeId: GuidedFlowId,
  docAvailable: boolean,
  defaultDocumentContextEnabled: boolean,
): { thread: Thread; warning?: string } | null {
  const flowResult = startGuidedFlow(modeId);
  const t: Thread = {
    ...makeFreshThread(),
    title: guidedFlowTitle(modeId),
    messages: guidedBlocksToMessages(flowResult.blocks),
    guidedFlow: flowResult.state,
    documentContextEnabled: docAvailable ? defaultDocumentContextEnabled : false,
    updatedAt: Date.now(),
  };
  return { thread: t };
}

function isWorkspaceState(value: unknown): value is WorkspaceState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<WorkspaceState>;
  return v.version === 1
    && Array.isArray(v.activeThreadIds)
    && typeof v.activeId === 'string'
    && !!v.threads
    && typeof v.threads === 'object';
}

function loadState(
  customerId: string,
  initialBotMessage: string,
  customerStatus?: string,
  customerEmail?: string,
): WorkspaceState {
  try {
    const raw = localStorage.getItem(storageKey(customerId));
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isWorkspaceState(parsed)) {
        const archived = parsed.archived ?? {};
        const threadIds = Object.keys(parsed.threads);
        let activeThreadIds = parsed.activeThreadIds.filter((id) => Boolean(parsed.threads[id]));
        let activeId = parsed.activeId;
        if (activeThreadIds.length === 0 && threadIds.length > 0) {
          activeId = threadIds.includes(parsed.activeId) ? parsed.activeId : threadIds[0];
          activeThreadIds = [activeId];
        }
        if (activeThreadIds.length > 0) {
          if (!activeThreadIds.includes(activeId) || !parsed.threads[activeId]) {
            activeId = activeThreadIds[activeThreadIds.length - 1];
          }
          return {
            ...parsed,
            activeId,
            activeThreadIds,
            archived,
          };
        }
      }
    }
  } catch { /* fall through to fresh state */ }
  const fresh = makeFreshThread();
  return {
    version: 1,
    activeThreadIds: [fresh.id],
    activeId: fresh.id,
    threads: { [fresh.id]: fresh },
    archived: {},
  };
}

function persistWorkspaceState(customerId: string, state: WorkspaceState): void {
  try {
    const key = storageKey(customerId);
    let stored: WorkspaceState | null = null;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (isWorkspaceState(parsed)) stored = parsed;
      }
    } catch {
      stored = null;
    }
    if (!shouldPersistWorkspace(state, stored)) return;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Quota oder privater Modus — Chat bleibt für diese Laufzeit im React-State.
  }
}

function trimMessages(msgs: Message[]): Message[] {
  if (msgs.length <= MAX_MESSAGES_PER_THREAD) return msgs;
  const head = msgs.length > 0 && msgs[0].role === 'bot' ? [msgs[0]] : [];
  const tail = msgs.slice(-(MAX_MESSAGES_PER_THREAD - head.length));
  return [...head, ...tail];
}

/** Dieselbe Würfel-Drehung wie Arko ON/off in der Akte: rotateX, 500ms, Perspektive 280. */
function HauptCubeFlip({
  open,
  onClick,
  label,
  testId,
  front,
  back,
}: {
  open: boolean;
  onClick: () => void;
  label: string;
  testId?: string;
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center [backface-visibility:hidden]';
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      aria-expanded={open}
      onClick={onClick}
      className="relative h-7 w-7 shrink-0 [perspective:280px]"
    >
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span
          className={`${face} rounded-md bg-violet-600 text-white`}
          style={{ transform: 'rotateX(180deg)' }}
        >
          {back}
        </span>
      </span>
    </button>
  );
}

const HAUPT_ICON =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55';

/** Ruhe: Breite 0. Hover oder Tastaturfokus nur dieser Zeile fährt die Symbole aus. */
const CHAT_ROW_ACTIONS = 'portal-chat-row-actions';
const CHAT_ROW_ACTIONS_INNER = 'portal-chat-row-actions-inner flex min-w-0 items-center gap-0.5';

const PortalArkoChatWorkspace: React.FC<PortalArkoChatWorkspaceProps> = ({
  customerId,
  webhookUrl,
  initialBotMessage,
  title,
  subtitle,
  accentColor = 'blue',
  mode,
  extraBody,
  judicatureOpen = false,
  backgroundAnalysisOpen = false,
  timeoutMs = PORTAL_ARKO_CHAT_TIMEOUT_MS,
  showQuickActions,
  showRechtsfrageQuickAction,
  documentContext,
  documentDisplayName,
  defaultDocumentContextEnabled = false,
  hideChatHeader = false,
  compactChrome = false,
  chatShellClassName = PORTAL_CHAT.shellInner,
  enableContractIntent,
  onContractGenerate,
  onExportSchreibenPdf,
  onExportSchreibenDocx,
  onOpenPortalDocument,
  onApplySynthesisToDocument,
  synthesisHasOpenDocument = false,
  onActiveIntentChange: onActiveIntentChangeParent,
  memcore = null,
  layoutVariant = 'default',
  followAppearance = false,
  cursorCustomerName,
  cursorKanzlei,
  customerStatus,
  customerEmail,
  documentExtractFingerprint,
  documentExtractRevision = 0,
  quellDokumentNcPath,
  quellDokumentPbId,
  akteNcPath,
  boundAkte = null,
  chatHome,
  freeDocumentColumn = false,
  akten = [],
  akteDocumentCount = 0,
  onBoundAkteChange,
  akteDocsPanelOpenNonce = 0,
  onOpenAkteDocument,
  onRenameAkteDocument,
  canWriteAkteDocuments = false,
  activeDocumentId,
  activeDocumentFullExtract = null,
  openDocumentsMeta,
  availableDocuments,
  onDocumentSelectionControllerChange,
  onSelectedDocumentIdsChange,
  extractingDocumentIds = [],
  onAnalyzeDocumentsReady,
  authToken,
  paragraphIndexMap,
  cursorSelection,
  onMarkDocumentAnalyzed,
}) => {
  const isCursorLayout = layoutVariant === 'cursor';
  const appearanceChoice = usePortalAppearance();
  const arkoLightClass = followAppearance ? portalArkoSurfaceClass(appearanceChoice) : '';
  const isCompactLayout = layoutVariant === 'compact' || compactChrome;
  const docAvailable = hasDocumentContext(documentContext);
  const [state, setState] = useState<WorkspaceState>(() =>
    loadState(customerId, initialBotMessage, customerStatus, customerEmail),
  );
  const [showArchive, setShowArchive] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [threadSearch, setThreadSearch] = useState('');
  const [activeIntentLabel, setActiveIntentLabel] = useState<string | null>(null);
  const [akteModeOpen, setAkteModeOpen] = useState(false);
  const [akteChatDocsOpen, setAkteChatDocsOpen] = useState(false);
  const [akteChatRenameId, setAkteChatRenameId] = useState<string | null>(null);
  const [akteChatRenameDraft, setAkteChatRenameDraft] = useState('');
  const saveTimer = useRef<number | null>(null);
  const latestStateRef = useRef(state);
  latestStateRef.current = state;
  const availableDocumentsById = useMemo(
    () => new Map((availableDocuments ?? []).map((doc) => [doc.documentId, doc] as const)),
    [availableDocuments],
  );
  // ── MEMCORE Modul B: Dialog-Zustände ───────────────────────────────────────
  /** Session-Dialog; pendingAction = ursprünglich gewünschte Beendigung (Nudge-Pfad). */
  const [memSession, setMemSession] = useState<
    { threadId: string; pendingAction: 'archive' | 'delete' | null } | null
  >(null);
  const [memNudge, setMemNudge] = useState<{ threadId: string; action: 'archive' | 'delete' } | null>(null);
  const [memThumb, setMemThumb] = useState<{ messageId: string; text: string } | null>(null);
  const [akteHandoffOpen, setAkteHandoffOpen] = useState(false);
  const [analysisHandoffSeed, setAnalysisHandoffSeed] = useState<string | null>(null);
  /** Stream 4: Fall-Gedächtnis-Panel (gepinnte Eckdaten ansehen/entfernen) auf/zu. */
  const [showCaseFacts, setShowCaseFacts] = useState(false);
  const akteBound = chatHome === 'haupt' ? false : Boolean(boundAkte?.id);
  const [akteDocsPanelOpen, setAkteDocsPanelOpen] = useState(() =>
    loadPortalAkteDocsPanelExpanded(customerId),
  );
  const [akteDocRenameId, setAkteDocRenameId] = useState<string | null>(null);
  const [akteDocRenameDraft, setAkteDocRenameDraft] = useState('');
  const [akteDocRenameBusy, setAkteDocRenameBusy] = useState(false);
  const akteDocsPanelNonceRef = useRef(0);
  const lastBoundAkteRef = useRef<PortalBoundAkte | null>(boundAkte ?? null);
  useEffect(() => {
    if (boundAkte?.id) lastBoundAkteRef.current = boundAkte;
  }, [boundAkte]);

  const handleActiveIntentChange = useCallback(
    (label: string | null) => {
      setActiveIntentLabel(label);
      onActiveIntentChangeParent?.(label);
    },
    [onActiveIntentChangeParent],
  );

  const commitHubMode = useCallback((modeId: string) => {
    const next = nextAkteHubModeId(modeId);
    if (next === undefined) return;
    setState((prev) => {
      const thread = prev.threads[prev.activeId];
      if (!thread || (thread.hubModeId || null) === next) return prev;
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [thread.id]: { ...thread, hubModeId: next, updatedAt: Date.now() },
        },
      };
    });
    setAkteModeOpen(false);
  }, []);

  useEffect(() => {
    if (!akteModeOpen && !akteChatDocsOpen) return;
    const onDown = (event: MouseEvent) => {
      const node = event.target instanceof Element ? event.target : null;
      const inMode = Boolean(node?.closest('[data-akte-pop="mode"]'));
      const inDocs = Boolean(node?.closest('[data-akte-pop="docs"]'));
      if (akteModeOpen && aktePopoversAfterOutsideClick(inMode) === 'close') setAkteModeOpen(false);
      if (akteChatDocsOpen && aktePopoversAfterOutsideClick(inDocs) === 'close') setAkteChatDocsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [akteModeOpen, akteChatDocsOpen]);

  // Defensiv: Wenn der Kunde im selben Tab wechselt (sehr selten), neu laden.
  useEffect(() => {
    setState(loadState(customerId, initialBotMessage, customerStatus, customerEmail));
    setShowArchive(false);
    setWarning(null);
    setAkteDocsPanelOpen(loadPortalAkteDocsPanelExpanded(customerId));
    setAkteDocRenameId(null);
    // initialBotMessage absichtlich nicht in deps — neuer Begruessungstext darf bestehende Threads nicht ueberschreiben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Aktenraum: gebundene Akte hydriert den persistierten Thread (auch nach Hard-Refresh).
  // Ungebundener Verlauf wird angehängt; ein leerer Neu-Thread darf keinen Chat mit Messages verdrängen.
  useEffect(() => {
    if (chatHome === 'haupt' || chatHome === 'akte') return;
    const akteId = boundAkte?.id?.trim() || '';
    if (!akteId) return;
    const ncPath = effectivePortalAkteNcPath(boundAkte);
    const title = akteThreadTitle(boundAkte?.title || '');
    setState((prev) => {
      const decision = decideBindAkteThread({
        akteId,
        activeId: prev.activeId,
        threads: prev.threads,
        archived: prev.archived,
      });
      const patchBoundFields = (thread: Thread): Thread => ({
        ...thread,
        akteId,
        title: thread.messages.some((m) => m.role === 'user') ? thread.title : title,
        documentContextEnabled: true,
        documentContextAkteNcPath: ncPath,
        aktenStand: thread.aktenStand ?? buildAkteThreadStand({
          selectedDocTitles: [],
          lastUserText: lastUserTextForStand(thread.messages),
          lastBotText: lastBotTextForStand(thread.messages),
        }),
        updatedAt: Date.now(),
      });

      if (decision.action === 'keep') {
        const current = prev.threads[decision.threadId];
        if (!current) return prev;
        const nextNc = ncPath;
        const nextTitle = current.messages.some((m) => m.role === 'user')
          ? current.title
          : (boundAkte?.title ? title : current.title);
        if (
          (current.documentContextAkteNcPath || null) === (nextNc || null)
          && current.title === nextTitle
          && (current.akteId || '') === akteId
        ) {
          return prev;
        }
        return {
          ...prev,
          threads: {
            ...prev.threads,
            [current.id]: {
              ...current,
              akteId,
              title: nextTitle,
              documentContextAkteNcPath: nextNc,
            },
          },
        };
      }

      if (decision.action === 'attach') {
        const current = prev.threads[decision.threadId];
        if (!current) return prev;
        return {
          ...prev,
          threads: { ...prev.threads, [current.id]: patchBoundFields(current) },
        };
      }

      if (decision.action === 'switch') {
        if (decision.fromArchive) {
          const arch = prev.archived[decision.threadId];
          if (!arch) return prev;
          if (
            !prev.activeThreadIds.includes(decision.threadId)
            && prev.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS
          ) {
            setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats — bitte einen archivieren, dann Akte wechseln.`);
            return prev;
          }
          const restored = patchBoundFields(threadFromArchive(arch));
          const { [decision.threadId]: _removed, ...restArchived } = prev.archived;
          const activeThreadIds = prev.activeThreadIds.includes(restored.id)
            ? prev.activeThreadIds
            : [...prev.activeThreadIds, restored.id];
          return {
            ...prev,
            activeId: restored.id,
            activeThreadIds,
            threads: { ...prev.threads, [restored.id]: restored },
            archived: restArchived,
          };
        }
        const existing = prev.threads[decision.threadId];
        if (!existing) return prev;
        const activeThreadIds = prev.activeThreadIds.includes(existing.id)
          ? prev.activeThreadIds
          : [...prev.activeThreadIds, existing.id];
        return {
          ...prev,
          activeId: existing.id,
          activeThreadIds,
          threads: {
            ...prev.threads,
            [existing.id]: patchBoundFields(existing),
          },
        };
      }

      if (prev.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
        setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats — bitte einen archivieren, dann Akte wechseln.`);
        return prev;
      }
      const t: Thread = {
        ...makeFreshThread(),
        akteId,
        title,
        documentContextEnabled: true,
        documentContextAkteNcPath: ncPath,
        aktenStand: buildAkteThreadStand({ selectedDocTitles: [] }),
      };
      return {
        ...prev,
        activeThreadIds: [...prev.activeThreadIds, t.id],
        activeId: t.id,
        threads: { ...prev.threads, [t.id]: t },
      };
    });
  }, [boundAkte?.id, boundAkte?.title, boundAkte?.nc_path, chatHome]);

  useEffect(() => {
    if (chatHome !== 'haupt' && chatHome !== 'akte') return;
    const akteId = chatHome === 'akte' ? (boundAkte?.id || '').trim() : '';
    setState((prev) => {
      const current = prev.threads[prev.activeId];
      const currentOk = arkoThreadVisibleInScope(
        current,
        chatHome === 'haupt' ? { kind: 'haupt' } : { kind: 'akte', akteId },
      );
      if (currentOk) return prev;
      const next = prev.activeThreadIds
        .map((id) => prev.threads[id])
        .find((thread) => arkoThreadVisibleInScope(
          thread,
          chatHome === 'haupt' ? { kind: 'haupt' } : { kind: 'akte', akteId },
        ));
      if (!next) {
        if (!prev.activeId) return prev;
        return { ...prev, activeId: '' };
      }
      if (prev.activeId === next.id) return prev;
      return { ...prev, activeId: next.id };
    });
  }, [boundAkte?.id, chatHome]);

  /** Phase 1.3: Kachel-Klick → immer neuer Thread mit geführtem Flow. */
  const startGuidedFlowThread = useCallback(
    (modeId: GuidedFlowId) => {
      const patch = buildGuidedFlowThreadPatch(
        modeId,
        docAvailable || Boolean(boundAkte?.id),
        defaultDocumentContextEnabled,
      );
      if (!patch) return;
      setState((prev) => {
        if (prev.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
          setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte einen archivieren oder löschen.`);
          return prev;
        }
        setWarning(null);
        const t = patch.thread;
        return {
          ...prev,
          activeThreadIds: [...prev.activeThreadIds, t.id],
          activeId: t.id,
          threads: { ...prev.threads, [t.id]: t },
        };
      });
    },
    [boundAkte?.id, defaultDocumentContextEnabled, docAvailable],
  );

  useEffect(() => {
    const pending = consumePendingGuidedFlow();
    if (pending && isGuidedFlowId(pending)) {
      startGuidedFlowThread(pending);
    }
    const onGuidedFlow = (e: Event) => {
      const detail = (e as CustomEvent<ArkoHubIntentDetail>).detail;
      if (!detail?.modeId || !isGuidedFlowId(detail.modeId)) return;
      startGuidedFlowThread(detail.modeId);
    };
    window.addEventListener(ARKO_GUIDED_FLOW_EVENT, onGuidedFlow);
    return () => window.removeEventListener(ARKO_GUIDED_FLOW_EVENT, onGuidedFlow);
  }, [startGuidedFlowThread]);

  // View-Wechsel: Default übernehmen, sofern der Nutzer nicht explizit überschrieben hat.
  useEffect(() => {
    if (!docAvailable && !boundAkte?.id) return;
    setState(prev => {
      let changed = false;
      const newThreads = { ...prev.threads };
      for (const [id, thread] of Object.entries(prev.threads)) {
        if (thread.documentContextUserOverride) continue;
        if (thread.documentContextEnabled !== defaultDocumentContextEnabled) {
          newThreads[id] = { ...thread, documentContextEnabled: defaultDocumentContextEnabled };
          changed = true;
        }
      }
      return changed ? { ...prev, threads: newThreads } : prev;
    });
  }, [boundAkte?.id, defaultDocumentContextEnabled, docAvailable]);

  useEffect(() => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      persistWorkspaceState(customerId, state);
      saveTimer.current = null;
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [state, customerId]);

  // Chat- und Dokument-Workspace mounten sich beim View-Wechsel gegenseitig neu.
  // Flush im Layout-Cleanup, damit der neue Mount den Stand schon lesen kann.
  // Leerer Neu-Thread darf keinen Verlauf mit User-Messages überschreiben.
  useLayoutEffect(() => {
    const flush = () => {
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      persistWorkspaceState(customerId, latestStateRef.current);
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onHide);

    const stored = loadState(customerId, initialBotMessage, customerStatus, customerEmail);
    if (
      (workspaceHasUserMessages(stored) && !workspaceHasUserMessages(latestStateRef.current))
      || (workspaceHasMessages(stored) && !workspaceHasMessages(latestStateRef.current))
    ) {
      setState(stored);
    }

    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onHide);
      flush();
    };
    // initialBotMessage/Status/Email: nur Snapshot-Lesen, kein Reset bei Begruessungstext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  /** Fabrik fuer Thread-spezifische Setter. Wichtig: bindet an `threadId`, nicht an `activeId` —
   *  damit eine Antwort, die nach Tab-Wechsel eintrifft, nicht im falschen Tab landet. */
  const makeThreadSetter = useCallback(
    (threadId: string): React.Dispatch<React.SetStateAction<Message[]>> =>
      (value) => {
        setState(prev => {
          const current = prev.threads[threadId];
          if (!current) return prev;
          const next = typeof value === 'function'
            ? (value as (p: Message[]) => Message[])(current.messages)
            : value;
          const docCtxEnabled = resolveDocumentContextEnabled(
            current,
            defaultDocumentContextEnabled,
            docAvailable,
            (current.selectedDocumentIds ?? []).filter(
              (id) => aktePromptHasExtractBody(availableDocumentsById.get(id)?.documentContext),
            ).length >= 1,
            Boolean(boundAkte?.id),
          );
          const withDemo = maybeAppendDemoFristCard(
            next,
            customerStatus,
            customerEmail,
            docCtxEnabled ? documentContext : null,
          );
          const trimmed = trimMessages(withDemo);
          const nextStand = nextAkteStandForThread(
            current,
            trimmed,
            availableDocumentsById,
            documentDisplayName,
          );
          const updated: Thread = {
            ...current,
            messages: trimmed,
            updatedAt: Date.now(),
            title: deriveTitleFromMessages(trimmed, current.title),
            ...(nextStand
              ? {
                  aktenStand: akteStandContentEquals(current.aktenStand, nextStand)
                    ? current.aktenStand
                    : nextStand,
                }
              : {}),
          };
          return { ...prev, threads: { ...prev.threads, [threadId]: updated } };
        });
      },
    [
      availableDocumentsById,
      boundAkte?.id,
      customerEmail,
      customerStatus,
      documentContext,
      documentDisplayName,
      docAvailable,
      defaultDocumentContextEnabled,
    ],
  );

  const getThreadMessages = useCallback(
    (threadId: string) => state.threads[threadId]?.messages,
    [state.threads],
  );

  const appendToThread = useCallback(
    (threadId: string, updater: (prev: Message[]) => Message[]) => {
      makeThreadSetter(threadId)(updater);
    },
    [makeThreadSetter],
  );

  const revealAnalysisInChat = useCallback(() => {
    setAkteDocsPanelOpen(false);
    savePortalAkteDocsPanelExpanded(customerId, false);
  }, [customerId]);

  const {
    analysisViews,
    openJobId,
    setOpenJobId,
    completionNotices,
    canEnqueue,
    hasRunningJobs,
    enqueueJob,
    cancelJob,
    dismissFinished,
    dismissNotice,
  } = usePortalBackgroundJobsContext();

  useBindPortalBackgroundLiveThread({
    getThreadMessages,
    appendToThread,
    onAnalysisPostedToChat: revealAnalysisInChat,
    activeThreadId: () => {
      const current = latestStateRef.current;
      const id = current.activeId;
      return current.threads[id] ? id : null;
    },
  });

  const activeThread = state.threads[state.activeId];
  const validOpenDocumentIds = useMemo(
    () => new Set((openDocumentsMeta ?? []).map((doc) => doc.documentId)),
    [openDocumentsMeta],
  );
  const activeSelectedDocumentIds = useMemo(
    () => (activeThread?.selectedDocumentIds ?? []).filter((id) => validOpenDocumentIds.has(id)),
    [activeThread?.selectedDocumentIds, validOpenDocumentIds],
  );
  const activeSelectedDocuments = useMemo(
    () => activeSelectedDocumentIds
      .map((id) => availableDocumentsById.get(id))
      .filter((doc): doc is PortalSelectedDocumentPayload => Boolean(doc)),
    [activeSelectedDocumentIds, availableDocumentsById],
  );
  const activeSynthesisDocumentLabels = useMemo(
    () => formatSynthesisDocumentLabels(activeSelectedDocuments.map((doc) => doc.title)),
    [activeSelectedDocuments],
  );

  useEffect(() => {
    if (!activeThread?.akteId) return;
    const computed = nextAkteStandForThread(
      activeThread,
      activeThread.messages,
      availableDocumentsById,
      documentDisplayName,
    );
    if (!computed || akteStandContentEquals(activeThread.aktenStand, computed)) return;
    const threadId = activeThread.id;
    setState((prev) => {
      const t = prev.threads[threadId];
      if (!t?.akteId) return prev;
      const next = nextAkteStandForThread(t, t.messages, availableDocumentsById, documentDisplayName);
      if (!next || akteStandContentEquals(t.aktenStand, next)) return prev;
      return {
        ...prev,
        threads: { ...prev.threads, [threadId]: { ...t, aktenStand: next } },
      };
    });
  }, [activeThread, availableDocumentsById, documentDisplayName]);

  const documentContextForPrompt = useMemo(
    () => (akteBound
      ? appendAkteStandToDocumentContext(documentContext, activeThread?.aktenStand)
      : documentContext),
    [akteBound, documentContext, activeThread?.aktenStand],
  );

  // Geschlossene Tabs dürfen in keinem Thread als unsichtbarer Payload-Kontext
  // weiterleben. Nicht prune'n solange noch keine Docs hydriert sind (sonst Hard-Refresh
  // löscht die persistierte Auswahl bevor der Aktenspeicher da ist).
  useEffect(() => {
    if (validOpenDocumentIds.size === 0) return;
    setState((prev) => {
      let changed = false;
      const threads = { ...prev.threads };
      for (const [threadId, thread] of Object.entries(prev.threads)) {
        const current = thread.selectedDocumentIds ?? [];
        const selectedDocumentIds = current.filter((id) => validOpenDocumentIds.has(id));
        if (selectedDocumentIds.length === current.length) continue;
        changed = true;
        threads[threadId] = {
          ...thread,
          selectedDocumentIds,
          documentContextAkteNcPath:
            selectedDocumentIds.length > 0 ? thread.documentContextAkteNcPath : null,
          updatedAt: Date.now(),
        };
      }
      return changed ? { ...prev, threads } : prev;
    });
  }, [validOpenDocumentIds]);

  const toggleSelectedDocumentForActiveThread = useCallback((rawDocumentId: string) => {
    const documentId = rawDocumentId.trim();
    if (!documentId || !validOpenDocumentIds.has(documentId)) return;
    setState((prev) => {
      const thread = prev.threads[prev.activeId];
      if (!thread) return prev;
      const current = (thread.selectedDocumentIds ?? []).filter((id) => validOpenDocumentIds.has(id));
      const alreadySelected = current.includes(documentId);
      if (!alreadySelected && current.length >= PORTAL_SYNTHESIS_MAX_SELECTED_DOCS) {
        setWarning(`Maximal ${PORTAL_SYNTHESIS_MAX_SELECTED_DOCS} Dokumente pro ARKO-Thread auswählbar.`);
        return prev;
      }
      const selectedDocumentIds = alreadySelected
        ? current.filter((id) => id !== documentId)
        : [...current, documentId];
      setWarning(null);
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [thread.id]: {
            ...thread,
            selectedDocumentIds,
            documentContextAkteNcPath:
              selectedDocumentIds.length > 0
                ? (boundAkte?.id ? (akteNcPath?.trim() || null) : (akteNcPath?.trim() || thread.documentContextAkteNcPath || null))
                : null,
            updatedAt: Date.now(),
          },
        },
      };
    });
  }, [akteNcPath, validOpenDocumentIds]);

  const lastFocusAutoSelectRef = useRef<string | null>(null);
  useEffect(() => {
    const id = activeDocumentId?.trim() || '';
    if (!id || !validOpenDocumentIds.has(id)) return;
    if (lastFocusAutoSelectRef.current === id) return;
    lastFocusAutoSelectRef.current = id;
    setState((prev) => {
      const thread = prev.threads[prev.activeId];
      if (!thread) return prev;
      if (!shouldAutoSelectFocusDocument(thread)) return prev;
      const current = (thread.selectedDocumentIds ?? []).filter((docId) => validOpenDocumentIds.has(docId));
      if (current.includes(id)) return prev;
      if (current.length >= PORTAL_SYNTHESIS_MAX_SELECTED_DOCS) return prev;
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [thread.id]: {
            ...thread,
            selectedDocumentIds: [...current, id],
            documentContextAkteNcPath: boundAkte?.id
              ? (akteNcPath?.trim() || null)
              : (akteNcPath?.trim() || thread.documentContextAkteNcPath || null),
            updatedAt: Date.now(),
          },
        },
      };
    });
  }, [activeDocumentId, akteNcPath, validOpenDocumentIds]);

  useEffect(() => {
    onSelectedDocumentIdsChange?.([...activeSelectedDocumentIds]);
  }, [activeSelectedDocumentIds, onSelectedDocumentIdsChange]);

  useEffect(() => {
    if (!onDocumentSelectionControllerChange) return;
    onDocumentSelectionControllerChange(toggleSelectedDocumentForActiveThread);
    return () => onDocumentSelectionControllerChange(null);
  }, [onDocumentSelectionControllerChange, toggleSelectedDocumentForActiveThread]);

  useEffect(() => {
    if (!akteDocsPanelOpenNonce) return;
    const isNewOpen = akteDocsPanelOpenNonce !== akteDocsPanelNonceRef.current;
    if (!isNewOpen) return;
    akteDocsPanelNonceRef.current = akteDocsPanelOpenNonce;
    setAkteDocsPanelOpen(true);
    savePortalAkteDocsPanelExpanded(customerId, true);
    setState((prev) => {
      const thread = prev.threads[prev.activeId];
      if (!thread) return prev;
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [thread.id]: {
            ...thread,
            documentContextEnabled: true,
            documentContextUserOverride: true,
            documentContextAkteNcPath: effectivePortalAkteNcPath(boundAkte),
            updatedAt: Date.now(),
          },
        },
      };
    });
  }, [akteDocsPanelOpenNonce, boundAkte, customerId]);

  const activeMessagesSetter = useMemo(
    () => makeThreadSetter(activeThread?.id ?? ''),
    [activeThread?.id, makeThreadSetter],
  );

  // ── Stream 4: Fall-Eckdaten (gepinnte Thread-Fakten, Auto-Vorbefüllung) ─────
  const activeCaseFacts = activeThread?.caseFacts ?? [];
  /** Gepinnte Fakten des aktiven Threads als memContext (stabil pro Inhalt). */
  const activePinnedMemContext = useMemo<MemProofItem[]>(
    () => (memcore ? activeCaseFacts.map(pinnedFactToMemItem) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memcore, JSON.stringify(activeCaseFacts)],
  );

  const pinFactToActiveThread = useCallback(
    (text: string) => {
      if (!memcore || !activeThread) return;
      const fact = makePinnedFact(text);
      if (!fact) return;
      const threadId = activeThread.id;
      setState(prev => {
        const t = prev.threads[threadId];
        if (!t) return prev;
        const nextFacts = addPinnedFact(t.caseFacts ?? [], fact);
        if (nextFacts === (t.caseFacts ?? [])) return prev;
        return { ...prev, threads: { ...prev.threads, [threadId]: { ...t, caseFacts: nextFacts } } };
      });
    },
    [memcore, activeThread],
  );

  const removeCaseFact = useCallback(
    (factId: string) => {
      if (!activeThread) return;
      const threadId = activeThread.id;
      setState(prev => {
        const t = prev.threads[threadId];
        if (!t || !t.caseFacts) return prev;
        return { ...prev, threads: { ...prev.threads, [threadId]: { ...t, caseFacts: removePinnedFact(t.caseFacts, factId) } } };
      });
    },
    [activeThread],
  );

  const handleGuidedFlowStateChange = useCallback((next: GuidedFlowState) => {
    if (!activeThread) return;
    const threadId = activeThread.id;
    setState((prev) => {
      const t = prev.threads[threadId];
      if (!t) return prev;
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [threadId]: {
            ...t,
            guidedFlow: next.active ? next : undefined,
            updatedAt: Date.now(),
          },
        },
      };
    });
  }, [activeThread]);

  const newChat = () => {
    if (state.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
      setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte einen archivieren oder löschen, dann neu starten.`);
      return;
    }
    if (chatHome === 'akte') {
      const akteId = newArkoChatAkteId({ kind: 'akte', akteId: boundAkte?.id || '' });
      if (!akteId) return;
      setState((prev) => {
        const t: Thread = {
          ...makeFreshThread(),
          akteId,
          documentContextEnabled: true,
          documentContextAkteNcPath: effectivePortalAkteNcPath(boundAkte),
        };
        return {
          ...prev,
          activeThreadIds: [...prev.activeThreadIds, t.id],
          activeId: t.id,
          threads: { ...prev.threads, [t.id]: t },
        };
      });
      setWarning(null);
      return;
    }
    if (chatHome === 'haupt') {
      setState((prev) => {
        const t: Thread = {
          ...makeFreshThread(),
          ...plusChatUnboundFields(),
        };
        return {
          ...prev,
          activeThreadIds: [...prev.activeThreadIds, t.id],
          activeId: t.id,
          threads: { ...prev.threads, [t.id]: t },
        };
      });
      setWarning(null);
      return;
    }
    // Plus = ungebundener Chat. Unbind zuerst, sonst hydriert boundAkte den leeren Thread wieder an.
    onBoundAkteChange?.(null);
    setState(prev => {
      const t: Thread = {
        ...makeFreshThread(),
        ...plusChatUnboundFields(),
      };
      return {
        ...prev,
        activeThreadIds: [...prev.activeThreadIds, t.id],
        activeId: t.id,
        threads: { ...prev.threads, [t.id]: t },
      };
    });
    setWarning(null);
  };

  const openAkteHandoff = () => {
    if (!activeThread) return;
    const akteId = (boundAkte?.id || activeThread.akteId || '').trim();
    if (!akteId) return;
    if (!hatSpeicherbarenInhalt(activeThread.messages) && !activeThread.akteHandoff?.trim()) return;
    setAnalysisHandoffSeed(null);
    setAkteHandoffOpen(true);
  };

  const openAnalysisWeiterarbeiten = (text: string) => {
    const body = text.trim();
    if (!body || !activeThread) return;
    const akteId = (boundAkte?.id || activeThread.akteId || '').trim();
    if (!akteId) return;
    setAnalysisHandoffSeed(body);
    setAkteHandoffOpen(true);
  };

  const copyAnalysisText = (text: string) => {
    const body = text.trim();
    if (!body || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    void navigator.clipboard.writeText(body);
  };

  const continueAkteFromHandoff = (handoffText: string) => {
    const source = activeThread;
    const akteId = (boundAkte?.id || source?.akteId || '').trim();
    if (!akteId || !source) {
      setAkteHandoffOpen(false);
      setAnalysisHandoffSeed(null);
      return;
    }
    const ncPath = source.documentContextAkteNcPath ?? effectivePortalAkteNcPath(boundAkte);
    setState((prev) => {
      const old = prev.threads[source.id];
      if (!old) return prev;
      const t: Thread = {
        ...makeFreshThread(),
        ...continueAkteChatFields(akteId, handoffText),
        title: old.title,
        documentContextAkteNcPath: ncPath,
        caseFacts: old.caseFacts,
        aktenStand: buildAkteThreadStand({ selectedDocTitles: [] }),
      };
      const { [old.id]: _removed, ...restThreads } = prev.threads;
      const activeThreadIds = prev.activeThreadIds
        .filter((id) => id !== old.id)
        .concat(t.id);
      if (activeThreadIds.length > PORTAL_MAX_OPEN_CHATS) {
        setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte einen archivieren.`);
        return prev;
      }
      return {
        ...prev,
        activeId: t.id,
        activeThreadIds,
        threads: { ...restThreads, [t.id]: t },
        archived: {
          ...prev.archived,
          [old.id]: { ...old, handedOff: true, archivedAt: Date.now() },
        },
      };
    });
    setAkteHandoffOpen(false);
    setAnalysisHandoffSeed(null);
    setWarning(null);
  };

  const switchTo = (id: string) => {
    const next = state.threads[id];
    setState(prev => prev.activeId === id ? prev : { ...prev, activeId: id });
    setWarning(null);
    if (!next || chatHome) return;
    const nextAkteId = next.akteId?.trim() || '';
    if (!nextAkteId) {
      if (boundAkte?.id) onBoundAkteChange?.(null);
      return;
    }
    if (boundAkte?.id === nextAkteId) return;
    const fromList = akten.find((row) => row.id === nextAkteId);
    onBoundAkteChange?.(fromList ?? {
      id: nextAkteId,
      title: next.title.replace(/^Akte:\s*/, '') || next.title,
      nc_path: effectivePortalAkteNcPath({
        id: nextAkteId,
        nc_path: next.documentContextAkteNcPath ?? null,
      }),
    });
  };

  // ── MEMCORE Modul B: Nudge-Logik (mindestens 1× fragen, kein stilles Speichern §7.7) ──

  const markMemNudgeDone = useCallback((threadId: string) => {
    setState(prev => {
      const t = prev.threads[threadId];
      if (!t || t.memNudgeDone) return prev;
      return { ...prev, threads: { ...prev.threads, [threadId]: { ...t, memNudgeDone: true } } };
    });
  }, []);

  /** Nudge nötig? Nur bei aktivem MEMCORE, speicherbarem Inhalt und noch nie gefragt. */
  const memNudgeNoetig = useCallback(
    (threadId: string): boolean => {
      if (!memcore) return false;
      const t = state.threads[threadId];
      return Boolean(t && !t.memNudgeDone && hatSpeicherbarenInhalt(t.messages));
    },
    [memcore, state.threads],
  );

  // Neuladen/Schließen des Tabs: generischer Browser-Prompt als Mindest-Nudge,
  // solange ein aktiver Thread speicherbaren Inhalt hat und noch nie gefragt wurde.
  useEffect(() => {
    if (!memcore) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const offen = state.activeThreadIds.some(id => {
        const t = state.threads[id];
        return t && !t.memNudgeDone && hatSpeicherbarenInhalt(t.messages);
      });
      if (offen) {
        e.preventDefault();
        // Chromium verlangt returnValue für den Dialog
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [memcore, state.activeThreadIds, state.threads]);

  const archiveThread = (id: string) => {
    if (memNudgeNoetig(id)) {
      setMemNudge({ threadId: id, action: 'archive' });
      return;
    }
    archiveThreadNow(id);
  };

  const archiveThreadNow = (id: string) => {
    setState(prev => {
      const t = prev.threads[id];
      if (!t) return prev;
      const remainingActive = prev.activeThreadIds.filter(x => x !== id);
      const newThreads = { ...prev.threads };
      delete newThreads[id];
      const archived: ArchivedThread = { ...t, archivedAt: Date.now() };
      if (remainingActive.length === 0) {
        if (chatHome === 'akte') {
          return {
            ...prev,
            activeThreadIds: [],
            activeId: '',
            threads: newThreads,
            archived: { ...prev.archived, [id]: archived },
          };
        }
        const fresh = {
          ...makeFreshThread(),
          ...(chatHome === 'haupt' ? plusChatUnboundFields() : {}),
        };
        return {
          ...prev,
          activeThreadIds: [fresh.id],
          activeId: fresh.id,
          threads: { [fresh.id]: fresh },
          archived: { ...prev.archived, [id]: archived },
        };
      }
      const newActive = prev.activeId === id ? remainingActive[remainingActive.length - 1] : prev.activeId;
      return {
        ...prev,
        activeThreadIds: remainingActive,
        activeId: newActive,
        threads: newThreads,
        archived: { ...prev.archived, [id]: archived },
      };
    });
    setWarning(null);
  };

  const deleteThread = (id: string) => {
    if (memNudgeNoetig(id)) {
      setMemNudge({ threadId: id, action: 'delete' });
      return;
    }
    deleteThreadNow(id);
  };

  const deleteThreadNow = (id: string) => {
    if (!window.confirm('Diesen Chat endgültig löschen?')) return;
    setState(prev => {
      const remainingActive = prev.activeThreadIds.filter(x => x !== id);
      const newThreads = { ...prev.threads };
      delete newThreads[id];
      if (remainingActive.length === 0) {
        if (chatHome === 'akte') {
          return {
            ...prev,
            activeThreadIds: [],
            activeId: '',
            threads: newThreads,
          };
        }
        const fresh = {
          ...makeFreshThread(),
          ...(chatHome === 'haupt' ? plusChatUnboundFields() : {}),
        };
        return {
          ...prev,
          activeThreadIds: [fresh.id],
          activeId: fresh.id,
          threads: { [fresh.id]: fresh },
        };
      }
      const newActive = prev.activeId === id ? remainingActive[remainingActive.length - 1] : prev.activeId;
      return {
        ...prev,
        activeThreadIds: remainingActive,
        activeId: newActive,
        threads: newThreads,
      };
    });
    setWarning(null);
  };

  const restoreFromArchive = (id: string) => {
    if (state.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
      setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte erst einen archivieren oder löschen.`);
      return;
    }
    const archPreview = state.archived[id];
    setState(prev => {
      const arch = prev.archived[id];
      if (!arch) return prev;
      const newArchived = { ...prev.archived };
      delete newArchived[id];
      const restored: Thread = {
        id: arch.id,
        title: arch.title,
        messages: arch.messages,
        updatedAt: Date.now(),
        documentContextEnabled: arch.documentContextEnabled,
        documentContextUserOverride: arch.documentContextUserOverride,
        selectedDocumentIds: arch.selectedDocumentIds,
        documentContextAkteNcPath: arch.documentContextAkteNcPath,
        akteId: arch.akteId,
        aktenStand: arch.aktenStand,
        caseFacts: arch.caseFacts,
        guidedFlow: arch.guidedFlow,
        hubModeId: arch.hubModeId,
      };
      return {
        ...prev,
        activeThreadIds: [...prev.activeThreadIds, restored.id],
        activeId: restored.id,
        threads: { ...prev.threads, [restored.id]: restored },
        archived: newArchived,
      };
    });
    setWarning(null);
    const restoredAkteId = archPreview?.akteId?.trim() || '';
    if (!chatHome && restoredAkteId && restoredAkteId !== boundAkte?.id) {
      const fromList = akten.find((row) => row.id === restoredAkteId);
      onBoundAkteChange?.(fromList ?? {
        id: restoredAkteId,
        title: archPreview.title.replace(/^Akte:\s*/, '') || archPreview.title,
        nc_path: effectivePortalAkteNcPath({
          id: restoredAkteId,
          nc_path: archPreview.documentContextAkteNcPath ?? null,
        }),
      });
    }
  };

  const deleteArchived = (id: string) => {
    if (!window.confirm('Diesen archivierten Chat endgültig löschen?')) return;
    setState(prev => {
      const newArchived = { ...prev.archived };
      delete newArchived[id];
      return { ...prev, archived: newArchived };
    });
  };

  const archivedList = useMemo(() => {
    const rows = Object.values(state.archived).sort((a, b) => b.archivedAt - a.archivedAt);
    if (chatHome === 'haupt') {
      return rows.filter((row) => arkoThreadVisibleInScope(row, { kind: 'haupt' }));
    }
    if (chatHome === 'akte') {
      return rows.filter((row) => arkoThreadVisibleInScope(row, {
        kind: 'akte',
        akteId: boundAkte?.id || '',
      }));
    }
    return rows;
  }, [boundAkte?.id, chatHome, state.archived]);

  const activeTabs = state.activeThreadIds
    .map(id => state.threads[id])
    .filter((t): t is Thread => Boolean(t));
  const filteredActiveTabs = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    if (!q) return activeTabs;
    return activeTabs.filter((t) => t.title.toLowerCase().includes(q));
  }, [activeTabs, threadSearch]);
  const partitionedActiveTabs = useMemo(() => partitionArkoThreads(activeTabs), [activeTabs]);
  const partitionedFilteredTabs = useMemo(
    () => partitionArkoThreads(filteredActiveTabs),
    [filteredActiveTabs],
  );
  const scopedActiveTabs = useMemo(() => {
    if (chatHome === 'haupt') {
      return activeTabs.filter((thread) => arkoThreadVisibleInScope(thread, { kind: 'haupt' }));
    }
    if (chatHome === 'akte') {
      return activeTabs.filter((thread) => arkoThreadVisibleInScope(thread, {
        kind: 'akte',
        akteId: boundAkte?.id || '',
      }));
    }
    return activeTabs;
  }, [activeTabs, boundAkte?.id, chatHome]);
  const scopedFilteredTabs = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    const base = scopedActiveTabs;
    if (!q) return base;
    return base.filter((thread) => thread.title.toLowerCase().includes(q));
  }, [scopedActiveTabs, threadSearch]);
  const fullActive = activeTabs.length >= PORTAL_MAX_OPEN_CHATS;

  const selectedDocumentContextAvailable =
    activeSelectedDocuments.filter((doc) => aktePromptHasExtractBody(doc.documentContext)).length >= 1;
  const activeContextAvailable = docAvailable || selectedDocumentContextAvailable || Boolean(boundAkte?.id);
  const activeDocumentContextEnabled = activeThread
    ? resolveDocumentContextEnabled(
        activeThread,
        defaultDocumentContextEnabled,
        docAvailable,
        selectedDocumentContextAvailable,
        akteBound,
      )
    : false;
  const selectedDocumentsForActiveContext = activeDocumentContextEnabled
    ? activeSelectedDocuments
    : [];
  const synthesisDocumentLabelsForActiveContext = activeDocumentContextEnabled
    ? activeSynthesisDocumentLabels
    : null;

  const documentAnalyzeDone = useMemo(
    () => isDocumentAnalyzeDone(customerId, documentExtractFingerprint ?? null),
    [customerId, documentExtractFingerprint],
  );

  const prevActiveDocumentIdRef = useRef<string | null>(null);

  // Slice 1.3: Dokument-Fokus wechselt → Hinweis im globalen Chat, kein neuer Thread.
  useEffect(() => {
    const docId = activeDocumentId?.trim() || null;
    const prev = prevActiveDocumentIdRef.current;

    if (!docId || !docAvailable || !activeThread || !activeDocumentContextEnabled) return;
    if (prev === docId) return;
    prevActiveDocumentIdRef.current = docId;
    if (!prev) return;

    const name = documentDisplayName?.trim() || 'Dokument';
    const threadId = activeThread.id;
    setState((prevState) => {
      const t = prevState.threads[threadId];
      if (!t) return prevState;
      const hintText = `ARKO arbeitet jetzt mit: ${name}`;
      const last = t.messages[t.messages.length - 1];
      if (last?.role === 'bot' && last.text === hintText) return prevState;
      const messages = trimMessages([
        ...t.messages,
        {
          role: 'bot' as const,
          text: hintText,
          messageKind: 'document_focus_switch' as const,
          id: newMessageId(),
        },
      ]);
      return {
        ...prevState,
        threads: {
          ...prevState.threads,
          [threadId]: { ...t, messages, updatedAt: Date.now() },
        },
      };
    });
  }, [
    activeDocumentContextEnabled,
    activeDocumentId,
    docAvailable,
    documentDisplayName,
    activeThread,
  ]);

  // Keine stille Frist-Probe mehr beim Dokument-Öffnen — nur noch explizit per Chip/Intent.

  const startBackgroundDocumentAnalysis = useCallback(() => {
    if (!backgroundAnalysisOpen) return;
    if (!canEnqueue || !activeThread || !activeDocumentContextEnabled) return;

    const sourceContext = (text: string, fullRaw?: string | null) =>
      preferFullExtractForAnalysis(text, fullRaw);
    const liveFull = activeDocumentFullExtract?.text?.trim() || null;
    const liveMeta = {
      extractPages: activeDocumentFullExtract?.pages,
      extractRawChars: activeDocumentFullExtract?.rawBytes,
      extractTruncated: activeDocumentFullExtract?.truncated,
      extractSource: activeDocumentFullExtract?.source,
    };
    const fullFor = (docId: string, cached?: string | null) => {
      const fromCache = (cached || '').trim();
      if (docId === activeDocumentId?.trim() && liveFull && liveFull.length > fromCache.length) {
        return liveFull;
      }
      return fromCache || (docId === activeDocumentId?.trim() ? liveFull : null);
    };

    const checkedDocs = selectedDocumentsForActiveContext.filter((d) => hasUsableAkteExtract(d.documentContext));
    const useMultiSelect = checkedDocs.length >= 1;

    if (useMultiSelect) {
      const labels = formatSynthesisDocumentLabels(checkedDocs.map((d) => d.title))
        ?? checkedDocs.map((d) => d.title).join(' + ');
      const combinedContext = checkedDocs
        .map((d) => `--- ${d.title} ---\n${sourceContext(d.documentContext, fullFor(d.documentId, d.fullExtractText))}`)
        .join('\n\n');
      const primary = checkedDocs.find((d) => d.documentId === activeDocumentId?.trim())
        ?? checkedDocs[0];
      const primaryIsFocus = primary.documentId === activeDocumentId?.trim();

      enqueueJob({
        documentId: primary.documentId,
        documentName: labels,
        threadId: activeThread.id,
        documentContext: combinedContext,
        openDocumentsMeta,
        selectedDocuments: checkedDocs,
        documentExtractFingerprint: primary.documentExtractFingerprint ?? documentExtractFingerprint,
        extractPages: primary.extractPages || (primaryIsFocus ? liveMeta.extractPages : undefined),
        extractRawChars: primary.extractRawChars || (primaryIsFocus ? liveMeta.extractRawChars : undefined),
        extractTruncated: primary.extractTruncated ?? (primaryIsFocus ? liveMeta.extractTruncated : undefined),
        extractSource: primary.extractSource || (primaryIsFocus ? liveMeta.extractSource : undefined),
      });
      return;
    }

    if (!activeDocumentId?.trim() || !hasUsableAkteExtract(documentContext)) return;

    const focusDoc = selectedDocumentsForActiveContext.find(
      (d) => d.documentId === activeDocumentId.trim(),
    ) ?? availableDocumentsById.get(activeDocumentId.trim());
    const focusFull = fullFor(activeDocumentId.trim(), focusDoc?.fullExtractText);
    const perDocCtx = focusDoc?.documentContext || documentContext;

    enqueueBackgroundDocumentAnalysis({
      threadId: activeThread.id,
      documentId: activeDocumentId.trim(),
      documentName: documentDisplayName?.trim() || 'Dokument',
      documentContext: perDocCtx,
      fullExtractText: focusFull,
      canEnqueue: true,
      openDocumentsMeta,
      documentExtractFingerprint,
      extractPages: focusDoc?.extractPages || liveMeta.extractPages,
      extractRawChars: focusDoc?.extractRawChars || liveMeta.extractRawChars,
      extractTruncated: focusDoc?.extractTruncated ?? liveMeta.extractTruncated,
      extractSource: focusDoc?.extractSource || liveMeta.extractSource,
    }, enqueueJob);
  }, [
    canEnqueue,
    activeThread,
    activeDocumentContextEnabled,
    selectedDocumentsForActiveContext,
    activeDocumentId,
    activeDocumentFullExtract,
    documentContext,
    documentDisplayName,
    openDocumentsMeta,
    documentExtractFingerprint,
    availableDocumentsById,
    enqueueJob,
    backgroundAnalysisOpen,
  ]);

  const backgroundAnalysisReady = useMemo(() => {
    if (!activeDocumentContextEnabled) return false;
    const checkedDocs = selectedDocumentsForActiveContext.filter((d) => hasUsableAkteExtract(d.documentContext));
    if (checkedDocs.length >= 1) return true;
    return Boolean(activeDocumentId?.trim() && hasUsableAkteExtract(documentContext));
  }, [
    activeDocumentContextEnabled,
    selectedDocumentsForActiveContext,
    activeDocumentId,
    documentContext,
  ]);

  const backgroundAnalysisLabel = useMemo(() => {
    if (hasRunningJobs) return 'Analyse läuft…';
    const checkedCount = selectedDocumentsForActiveContext
      .filter((d) => hasUsableAkteExtract(d.documentContext)).length;
    if (checkedCount >= 2) return 'Ausgewählte im Hintergrund analysieren';
    if (checkedCount === 1) return 'Ausgewähltes im Hintergrund analysieren';
    return 'Im Hintergrund analysieren';
  }, [selectedDocumentsForActiveContext, hasRunningJobs]);

  const backgroundAnalysisTitle = useMemo(() => {
    if (!canEnqueue) return 'Maximal zwei Hintergrundanalysen gleichzeitig';
    if (!activeDocumentContextEnabled) return 'Dokument-Kontext ist ausgeschaltet';
    const checkedDocs = selectedDocumentsForActiveContext.filter((d) => hasUsableAkteExtract(d.documentContext));
    if (checkedDocs.length >= 1) {
      const labels = formatSynthesisDocumentLabels(checkedDocs.map((d) => d.title))
        ?? checkedDocs.map((d) => d.title).join(' + ');
      return `„${labels}“ im Hintergrund analysieren`;
    }
    if (hasUsableAkteExtract(documentContext)) {
      return `„${documentDisplayName?.trim() || 'Dokument'}“ im Hintergrund analysieren`;
    }
    return 'Dokumenttext wird noch geladen';
  }, [
    canEnqueue,
    activeDocumentContextEnabled,
    selectedDocumentsForActiveContext,
    documentContext,
    documentDisplayName,
  ]);

  const toggleAkteContext = (enabled: boolean) => {
    const nextId = nextAkteContextBindId({
      enabled,
      boundAkteId: boundAkte?.id,
      lastBoundAkteId: lastBoundAkteRef.current?.id,
      fallbackAkteId: akten[0]?.id,
    });
    if (!nextId) {
      onBoundAkteChange?.(null);
      return;
    }
    if (nextId === boundAkte?.id) return;
    const row = akten.find((akte) => akte.id === nextId) ?? lastBoundAkteRef.current;
    if (row) onBoundAkteChange?.(row);
  };

  const toggleDocumentContext = (enabled: boolean) => {
    if (!activeThread || !activeContextAvailable) return;
    setState(prev => {
      const t = prev.threads[activeThread.id];
      if (!t) return prev;
      const wasEnabled = resolveDocumentContextEnabled(
        t,
        defaultDocumentContextEnabled,
        docAvailable,
        selectedDocumentContextAvailable,
        akteBound,
      );
      let messages = t.messages;
      if (enabled && !wasEnabled) {
        const name = documentDisplayName?.trim() || boundAkte?.title?.trim() || 'Dokument';
        const refer = boundAkte?.title?.trim() && !documentDisplayName?.trim()
          ? `Ich beziehe mich ab jetzt auf die Akte ‚${name}'.`
          : `Ich beziehe mich ab jetzt auf ‚${name}'.`;
        messages = trimMessages([
          ...t.messages,
          { role: 'bot' as const, text: refer },
        ]);
      }
      const updated: Thread = {
        ...t,
        messages,
        documentContextEnabled: enabled,
        documentContextUserOverride: true,
        updatedAt: Date.now(),
        title: deriveTitleFromMessages(messages, t.title),
      };
      return { ...prev, threads: { ...prev.threads, [t.id]: updated } };
    });
  };

  const tabBtnClass = isCompactLayout
    ? 'px-1.5 py-0 rounded-none text-[10px] max-w-[140px]'
    : isCursorLayout
      ? 'px-2.5 py-2 rounded-none text-xs w-full max-w-none justify-start'
      : 'px-3 py-1.5 rounded-none text-xs max-w-[220px]';
  const tabIconClass = isCompactLayout ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5';

  const renderThreadTabActions = (t: Thread, isActive: boolean) => (
    <div className={CHAT_ROW_ACTIONS}>
      <div className={CHAT_ROW_ACTIONS_INNER}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          archiveThread(t.id);
        }}
        className="p-1 rounded hover:bg-amber-500/15 text-[#6c706c] hover:text-amber-300"
        title="Chat archivieren"
        aria-label="Chat archivieren"
      >
        <Archive className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          deleteThread(t.id);
        }}
        className="p-1 rounded hover:bg-rose-500/15 text-[#6c706c] hover:text-rose-300"
        title="Chat löschen"
        aria-label="Chat löschen"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      {memcore ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!hatSpeicherbarenInhalt(t.messages)) return;
            if (t.id !== state.activeId) switchTo(t.id);
            setMemSession({ threadId: t.id, pendingAction: 'archive' });
          }}
          disabled={!hatSpeicherbarenInhalt(t.messages)}
          className="p-1 rounded hover:bg-[#57c06a]/15 text-[#6c706c] hover:text-[#6fcb7a] disabled:opacity-30 disabled:cursor-not-allowed"
          title="Session beenden — Merkpunkte prüfen und speichern"
          aria-label="Session beenden und speichern"
        >
          <BookMarked className="w-3.5 h-3.5" />
        </button>
      ) : null}
      {memcore ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (t.id !== state.activeId) switchTo(t.id);
            setShowCaseFacts((s) => (isActive ? !s : true));
          }}
          className={`p-1 rounded hover:bg-[#57c06a]/15 ${
            isActive && showCaseFacts ? 'text-[#6fcb7a]' : 'text-[#6c706c] hover:text-[#6fcb7a]'
          }`}
          title="Fall-Gedächtnis — gemerkte Eckdaten dieses Chats"
          aria-label="Fall-Gedächtnis anzeigen"
        >
          <Brain className="w-3.5 h-3.5" />
        </button>
      ) : null}
      </div>
    </div>
  );

  const renderThreadTabs = (
    layout: 'default' | 'cursorTop',
    tabs: Thread[],
    kind: 'chat' | 'akte' = 'chat',
  ) => {
    const isAkte = kind === 'akte';
    return tabs.map((t) => {
      const isActive = t.id === state.activeId;
      if (layout === 'cursorTop') {
        const cursorChrome = isAkte
          ? isActive
            ? 'bg-transparent text-cyan-50 font-semibold'
            : 'bg-transparent text-amber-100/80 hover:text-cyan-50'
          : isActive
            ? 'bg-transparent text-white font-semibold'
            : 'bg-transparent text-[#9aa09a] hover:text-[#e6e7e5]';
        return (
          <div
            key={t.id}
            data-portal-side-active={isActive ? 'true' : 'false'}
            className={`group flex items-center flex-shrink-0 max-w-[min(100%,280px)] border-b-2 transition-colors ${
              isActive
                ? isAkte
                  ? 'border-cyan-300/80'
                  : 'border-violet-400/90'
                : 'border-transparent'
            } ${cursorChrome}`}
          >
            <button
              type="button"
              onClick={() => switchTo(t.id)}
              className={`truncate flex-1 text-left px-2 py-1.5 min-w-0 bg-transparent ${
                isActive ? 'text-sm font-semibold' : 'text-xs font-medium'
              }`}
              title={t.title}
            >
              {t.title}
            </button>
            {renderThreadTabActions(t, isActive)}
          </div>
        );
      }
      const defaultChrome = isAkte
        ? isActive
          ? 'bg-cyan-500/15 border-cyan-500/35 text-white ring-1 ring-cyan-400/30'
          : 'bg-amber-500/5 border-amber-500/20 text-amber-100/80 hover:bg-amber-500/10 hover:text-amber-50'
        : isActive
          ? PORTAL_TAB.active
          : PORTAL_TAB.idle;
      const Icon = isAkte ? Briefcase : MessageSquare;
      return (
        <div
          key={t.id}
          role="button"
          tabIndex={0}
          onClick={() => switchTo(t.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') switchTo(t.id);
          }}
          title={t.title}
          data-portal-side-active={isActive ? 'true' : 'false'}
          className={`group ${PORTAL_TAB.base} ${tabBtnClass} ${defaultChrome}`}
        >
          <Icon
            className={`${tabIconClass} flex-shrink-0 ${
              isAkte
                ? isActive
                  ? 'text-cyan-200'
                  : 'text-amber-300/80'
                : isActive
                  ? PORTAL_TAB.iconActive
                  : PORTAL_TAB.iconIdle
            }`}
          />
          <span className="truncate flex-1 text-left">{t.title}</span>
          <div className={CHAT_ROW_ACTIONS}>
            <div className={`${CHAT_ROW_ACTIONS_INNER} ml-0.5`}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                archiveThread(t.id);
              }}
              className="p-0.5 rounded hover:bg-slate-700/60 text-slate-400 hover:text-amber-300"
              title="Archivieren"
              aria-label="Chat archivieren"
            >
              <Archive className="w-2.5 h-2.5" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                deleteThread(t.id);
              }}
              className="p-0.5 rounded hover:bg-slate-700/60 text-slate-400 hover:text-rose-300"
              title="Löschen"
              aria-label="Chat löschen"
            >
              <X className="w-2.5 h-2.5" />
            </button>
            </div>
          </div>
        </div>
      );
    });
  };

  const renderPlusCursorButton = () => (
    <button
      type="button"
      onClick={newChat}
      disabled={fullActive}
      className={`flex items-center justify-center rounded-lg border border-white/[0.08] p-2 text-slate-200 hover:border-violet-500/30 hover:bg-violet-500/5 transition-colors flex-shrink-0 ${
        fullActive ? 'opacity-40 cursor-not-allowed' : ''
      }`}
      title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Neuer Chat'}
      aria-label="Neuer Chat"
    >
      <Plus className="w-4 h-4" />
    </button>
  );

  const renderThreadColumn = (
    kind: 'chat' | 'akte',
    tabs: Thread[],
    layout: 'default' | 'cursorTop',
    leading?: React.ReactNode,
  ) => {
    const isAkte = kind === 'akte';
    return (
      <section
        className={`min-w-0 rounded-lg border px-1.5 py-1 ${
          isAkte
            ? 'border-cyan-500/25 bg-cyan-500/[0.06]'
            : 'border-white/[0.06] bg-slate-950/30'
        }`}
        aria-label={isAkte ? 'Akten-Chats' : 'Chats'}
      >
        <div
          className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-0.5 pb-0.5 ${
            isAkte ? 'text-cyan-200/90' : 'text-slate-500'
          }`}
        >
          {isAkte ? <Briefcase className="w-3 h-3" aria-hidden /> : <MessageSquare className="w-3 h-3" aria-hidden />}
          {isAkte ? 'Akten' : 'Chats'}
        </div>
        <div className="flex items-center gap-1 min-w-0 overflow-x-auto custom-scrollbar">
          {leading}
          {tabs.length === 0 ? (
            <span className="text-[10px] text-slate-500 px-1 whitespace-nowrap">
              {isAkte ? 'Keine Akten-Chats' : 'Noch keine Chats'}
            </span>
          ) : (
            renderThreadTabs(layout, tabs, kind)
          )}
        </div>
      </section>
    );
  };

  const renderThreadColumns = (layout: 'default' | 'cursorTop') => {
    if (chatHome === 'haupt' || chatHome === 'akte' || freeDocumentColumn) {
      const source = layout === 'cursorTop' ? scopedFilteredTabs : scopedActiveTabs;
      const leading = layout === 'cursorTop' ? renderPlusCursorButton() : renderNewChatButton();
      return (
        <div className="min-w-0">
          {renderThreadColumn('chat', source, layout, leading)}
        </div>
      );
    }
    const source = layout === 'cursorTop' ? partitionedFilteredTabs : partitionedActiveTabs;
    const chatLeading = layout === 'cursorTop' ? renderPlusCursorButton() : renderNewChatButton();
    const akteLeading = renderContinueAkteButton(layout === 'cursorTop');
    return (
      <div className={THREAD_SPLIT_GRID}>
        {renderThreadColumn('chat', source.chats, layout, chatLeading)}
        {renderThreadColumn('akte', source.akten, layout, akteLeading)}
      </div>
    );
  };

  const renameAkteChatTitle = (id: string) => {
    const title = akteChatRenameDraft.trim();
    const openAkteId = boundAkte?.id || '';
    if (!title) return;
    setState((prev) => {
      const thread = prev.threads[id];
      if (!thread) return prev;
      const next = applyAkteChatAction(
        [{ id: thread.id, akteId: thread.akteId, title: thread.title }],
        { type: 'rename', id, title },
        openAkteId,
      );
      const renamed = next.find((row) => row.id === id);
      if (!renamed || renamed.title === thread.title) return prev;
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [id]: { ...thread, title: renamed.title, updatedAt: Date.now() },
        },
      };
    });
    setAkteChatRenameId(null);
  };

  const renderAkteCursorBar = () => (
    <header
      data-testid="portal-akte-arko-bar"
      className="flex-shrink-0 border-b border-white/[0.06] bg-transparent px-2 py-2"
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={newChat}
          disabled={fullActive}
          data-testid="portal-akte-arko-add"
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45"
          title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Chat dieser Akte'}
          aria-label="Chat dieser Akte"
        >
          <Plus className="h-3 w-3" aria-hidden />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" role="list">
          {scopedActiveTabs.length === 0 ? (
            <p className="m-0 px-1 text-[11px] text-slate-500">Noch kein Chat in dieser Akte.</p>
          ) : (
            scopedActiveTabs.map((thread) => {
              const selected = thread.id === state.activeId;
              const allowed = akteChatActionAllowed(thread, boundAkte?.id || '');
              const renaming = akteChatRenameId === thread.id;
              return (
                <div
                  key={thread.id}
                  role="listitem"
                  className={`group portal-chat-chip flex shrink-0 items-center border-b-2 ${
                    selected ? 'border-violet-400 bg-white/[0.08]' : 'border-transparent hover:bg-white/[0.04]'
                  }`}
                >
                  {renaming ? (
                    <input
                      value={akteChatRenameDraft}
                      onChange={(event) => setAkteChatRenameDraft(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') renameAkteChatTitle(thread.id);
                        if (event.key === 'Escape') setAkteChatRenameId(null);
                      }}
                      className="w-full min-w-0 rounded border border-white/15 bg-transparent px-2 py-1 text-[11px] text-slate-100"
                      aria-label="Chat umbenennen"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => switchTo(thread.id)}
                      className={`min-w-0 w-full truncate px-2 py-1.5 text-left text-[11px] ${
                        selected ? 'font-semibold text-slate-100' : 'text-slate-300'
                      }`}
                      title={thread.title}
                      aria-current={selected ? 'true' : undefined}
                    >
                      {thread.title}
                    </button>
                  )}
                  {allowed ? (
                    <div className={CHAT_ROW_ACTIONS}>
                      <div className={CHAT_ROW_ACTIONS_INNER}>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!akteChatActionAllowed(thread, boundAkte?.id || '')) return;
                          setAkteChatRenameId(thread.id);
                          setAkteChatRenameDraft(thread.title);
                        }}
                        className="rounded p-1 text-slate-400 hover:bg-white/[0.06] hover:text-slate-100"
                        title="Umbenennen"
                        aria-label="Chat umbenennen"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!akteChatActionAllowed(thread, boundAkte?.id || '')) return;
                          archiveThread(thread.id);
                        }}
                        className="rounded p-1 text-slate-400 hover:bg-amber-500/15 hover:text-amber-300"
                        title="Archivieren"
                        aria-label="Chat archivieren"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!akteChatActionAllowed(thread, boundAkte?.id || '')) return;
                          deleteThread(thread.id);
                        }}
                        className="rounded p-1 text-slate-400 hover:bg-rose-500/15 hover:text-rose-300"
                        title="Löschen"
                        aria-label="Chat löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
        {archivedList.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowArchive((open) => !open)}
            className="inline-flex shrink-0 items-center rounded-md border border-white/10 px-2 py-1 text-[10px] text-slate-300 hover:bg-white/[0.04]"
            title={`Archiv (${archivedList.length})`}
            aria-label={`Archiv (${archivedList.length})`}
            aria-expanded={showArchive}
          >
            <Archive className="h-3 w-3" aria-hidden />
          </button>
        ) : null}
      </div>
    </header>
  );

  const renderAkteStatusBar = () => {
    const orient = activeThread?.aktenStand?.orientation?.trim() || '';
    const orientShort = orient.length > 90 ? `${orient.slice(0, 88)}…` : orient;
    return (
      <div
        className="relative flex flex-shrink-0 flex-wrap items-center gap-1.5 border-b border-white/[0.06] bg-transparent px-2 py-2"
        data-testid="portal-akte-arko-status"
        aria-live="polite"
      >
        {backgroundAnalysisOpen && analysisViews.length > 0 ? renderBackgroundJobChips() : null}
        <div>
          <button
            type="button"
            data-testid="portal-akte-arko-mode"
            data-akte-pop="mode"
            aria-expanded={akteModeOpen}
            onClick={() => {
              setAkteChatDocsOpen(false);
              setAkteModeOpen((open) => !open);
            }}
            className="inline-flex items-center rounded-md border border-white/10 bg-transparent px-2 py-1 text-[10px] font-semibold text-slate-200 hover:bg-white/[0.04]"
          >
            {arkoHubModeLabel(activeThread?.hubModeId) || 'Kein Modus'}
          </button>
          {akteModeOpen ? (
            <div
              data-akte-pop="mode"
              className="absolute left-2 right-2 top-full z-30 mt-1 max-h-[28rem] overflow-y-auto rounded-lg border border-white/10 bg-[#07070a] p-2 shadow-lg"
              role="listbox"
              aria-label="Modus"
            >
              <button
                type="button"
                role="option"
                data-testid="portal-akte-arko-mode-none"
                aria-selected={!arkoHubModeLabel(activeThread?.hubModeId)}
                onClick={() => commitHubMode('')}
                className={`block w-full whitespace-normal rounded-md px-2 py-2 text-left text-[12px] ${
                  arkoHubModeLabel(activeThread?.hubModeId)
                    ? 'text-slate-300 hover:bg-white/[0.04]'
                    : 'bg-white/[0.08] text-slate-100'
                }`}
              >
                Kein Modus
              </button>
              {ARKO_HUB_MODES_LEGACY.map((mode) => {
                const selected = activeThread?.hubModeId === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-testid={`portal-akte-arko-mode-${mode.id}`}
                    onClick={() => commitHubMode(mode.id)}
                    className={`block w-full whitespace-normal rounded-md px-2 py-2 text-left text-[12px] ${
                      selected ? 'bg-white/[0.08] text-slate-100' : 'text-slate-300 hover:bg-white/[0.04]'
                    }`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <div>
          <button
            type="button"
            data-testid="portal-akte-arko-docs"
            data-akte-pop="docs"
            aria-expanded={akteChatDocsOpen}
            onClick={() => {
              setAkteModeOpen(false);
              setAkteChatDocsOpen((open) => !open);
            }}
            className="inline-flex items-center rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-500"
          >
            Dokumente
          </button>
          {akteChatDocsOpen ? (
            <div
              data-akte-pop="docs"
              className="absolute left-2 right-2 top-full z-30 mt-1 max-h-[32rem] overflow-y-auto rounded-lg border border-white/10 bg-[#07070a] p-3 shadow-lg"
              data-testid="portal-akte-arko-docs-list"
            >
              {(openDocumentsMeta ?? []).filter((doc) =>
                akteOnlyofficePlusKind(
                  { nextcloud_path: doc.nc_path },
                  { id: boundAkte?.id || '', nc_path: akteNcPath },
                ) === 'this-akte',
              ).length === 0 ? (
                <p className="m-0 px-1 text-[11px] text-slate-500">Keine Dokumente in dieser Akte.</p>
              ) : (
                (openDocumentsMeta ?? [])
                  .filter((doc) =>
                    akteOnlyofficePlusKind(
                      { nextcloud_path: doc.nc_path },
                      { id: boundAkte?.id || '', nc_path: akteNcPath },
                    ) === 'this-akte',
                  )
                  .map((doc) => {
                    const checked = activeSelectedDocumentIds.includes(doc.documentId);
                    const mark = portalFileTypeMark(doc.title);
                    const tone =
                      mark.tone === 'pdf'
                        ? 'bg-red-500/15 text-red-300'
                        : mark.tone === 'docx'
                          ? 'bg-blue-500/15 text-blue-200'
                          : 'bg-white/[0.06] text-slate-400';
                    return (
                      <label
                        key={doc.documentId}
                        className="flex items-start gap-2 rounded-md px-1 py-1.5 hover:bg-white/[0.04]"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          data-testid="portal-akte-arko-doc-check"
                          onChange={() => toggleSelectedDocumentForActiveThread(doc.documentId)}
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-white/20 bg-transparent text-violet-500"
                          aria-label={doc.title}
                        />
                        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${tone}`}>{mark.label}</span>
                        <span className="min-w-0 break-all text-[11px] text-slate-200">{doc.title}</span>
                      </label>
                    );
                  })
              )}
            </div>
          ) : null}
        </div>
        {orientShort ? (
          <span className="max-w-full break-words rounded-md border border-white/10 px-2 py-1 text-[10px] text-slate-400" title={orient}>
            {orientShort}
          </span>
        ) : null}
        {renderDocumentContextSwitch({ size: 'sm', testId: true })}
        {backgroundAnalysisOpen && activeContextAvailable ? (
          <button
            type="button"
            onClick={startBackgroundDocumentAnalysis}
            disabled={!canEnqueue || !backgroundAnalysisReady}
            className="inline-flex items-center gap-1 rounded-md border border-violet-400/35 bg-violet-500/10 px-2 py-1 text-[10px] font-semibold text-violet-200 hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-45"
            title={backgroundAnalysisTitle}
          >
            {hasRunningJobs ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Sparkles className="h-3 w-3" aria-hidden />}
            {backgroundAnalysisLabel}
          </button>
        ) : null}
      </div>
    );
  };

  const renderCursorTopBar = () => (
    <header
      data-testid="portal-haupt-arko-bar"
      className="arko-cursor-topbar flex-shrink-0 border-b border-violet-400/30 bg-[#07070a] px-2 py-2"
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={newChat}
          disabled={fullActive}
          data-testid="portal-haupt-arko-add"
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45"
          title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Neuer Chat'}
          aria-label="Neuer Chat"
        >
          <Plus className="h-3 w-3" aria-hidden />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" role="list">
          {scopedFilteredTabs.length === 0 ? (
            <p className="m-0 px-1 text-[11px] text-slate-500">Noch kein Chat.</p>
          ) : (
            scopedFilteredTabs.map((thread) => {
              const selected = thread.id === state.activeId;
              return (
                <div
                  key={thread.id}
                  role="listitem"
                  className={`group portal-chat-chip flex shrink-0 items-center border-b-2 ${
                    selected ? 'border-violet-400 bg-violet-600/20' : 'border-transparent hover:bg-white/[0.04]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => switchTo(thread.id)}
                    className={`min-w-0 w-full truncate px-2 py-1.5 text-left text-[11px] ${
                      selected ? 'font-semibold text-slate-100' : 'text-slate-300'
                    }`}
                    title={thread.title}
                    aria-current={selected ? 'true' : undefined}
                  >
                    {thread.title}
                  </button>
                  <div className={CHAT_ROW_ACTIONS}>
                    <div className={`${CHAT_ROW_ACTIONS_INNER} gap-0.5`}>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        archiveThread(thread.id);
                      }}
                      className={HAUPT_ICON}
                      title="Archivieren"
                      aria-label="Chat archivieren"
                    >
                      <Archive className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteThread(thread.id);
                      }}
                      className={HAUPT_ICON}
                      title="Löschen"
                      aria-label="Chat löschen"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    {memcore ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!hatSpeicherbarenInhalt(thread.messages)) return;
                          if (thread.id !== state.activeId) switchTo(thread.id);
                          setMemSession({ threadId: thread.id, pendingAction: 'archive' });
                        }}
                        disabled={!hatSpeicherbarenInhalt(thread.messages)}
                        className={`${HAUPT_ICON} disabled:cursor-not-allowed disabled:opacity-30`}
                        title="Session beenden — Merkpunkte prüfen und speichern"
                        aria-label="Session beenden und speichern"
                      >
                        <BookMarked className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                    {memcore ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (thread.id !== state.activeId) switchTo(thread.id);
                          setShowCaseFacts((open) => (selected ? !open : true));
                        }}
                        className={HAUPT_ICON}
                        title="Fall-Gedächtnis — gemerkte Eckdaten dieses Chats"
                        aria-label="Fall-Gedächtnis anzeigen"
                      >
                        <Brain className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="hidden w-[min(100%,11rem)] shrink-0 sm:block">
          <ArkoCursorThreadSearch value={threadSearch} onChange={setThreadSearch} tone="akte" />
        </div>
        {archivedList.length > 0 ? (
          <HauptCubeFlip
            open={showArchive}
            onClick={() => setShowArchive((open) => !open)}
            label={`Archiv (${archivedList.length})`}
            testId="portal-haupt-arko-archive"
            front={<Archive className="h-3.5 w-3.5" aria-hidden />}
            back={<Archive className="h-3.5 w-3.5" aria-hidden />}
          />
        ) : null}
      </div>
    </header>
  );

  const renderNewChatButton = (cursorRail = false) => (
    <button
      type="button"
      onClick={newChat}
      disabled={fullActive}
      className={`flex items-center gap-0.5 flex-shrink-0 ${
        cursorRail
          ? CURSOR_PORTAL.railBtn
          : isCompactLayout
            ? 'px-1 py-0 rounded-md text-[10px]'
            : 'px-2.5 py-1.5 rounded-lg text-xs'
      } ${
        fullActive && !cursorRail ? PORTAL_BTN.secondaryDisabled : cursorRail ? '' : PORTAL_BTN.secondary
      } ${fullActive && cursorRail ? 'opacity-40 cursor-not-allowed' : ''}`}
      title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Neuer Chat'}
    >
      <Plus className={isCompactLayout ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {!isCompactLayout && (cursorRail ? ' Neuer Chat' : ' Neuer Chat')}
    </button>
  );

  const renderContinueAkteButton = (cursorIcon = false) => {
    if (chatHome) return null;
    const bound = Boolean((boundAkte?.id || activeThread?.akteId || '').trim());
    if (!bound) return null;
    const ready = Boolean(
      activeThread
      && (hatSpeicherbarenInhalt(activeThread.messages) || activeThread.akteHandoff?.trim()),
    );
    return (
      <button
        type="button"
        onClick={openAkteHandoff}
        disabled={!ready || fullActive}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          cursorIcon
            ? 'justify-center rounded-lg border border-cyan-500/25 p-2 text-cyan-100 hover:border-cyan-400/40 hover:bg-cyan-500/10 transition-colors'
            : isCompactLayout
              ? 'px-1 py-0 rounded-md text-[10px]'
              : 'px-2.5 py-1.5 rounded-lg text-xs'
        } ${
          !cursorIcon
            ? (ready && !fullActive ? PORTAL_BTN.secondary : PORTAL_BTN.secondaryDisabled)
            : fullActive || !ready
              ? 'opacity-40 cursor-not-allowed'
              : ''
        }`}
        title={
          fullActive
            ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats`
            : ready
              ? 'Weiterarbeiten — Arbeitsstand verdichten, neuer Chat in dieser Akte (nicht Plus)'
              : 'Weiterarbeiten (noch kein Inhalt in diesem Akten-Chat)'
        }
        aria-label="Weiterarbeiten in dieser Akte"
      >
        <Redo2 className="w-3.5 h-3.5" />
        {!cursorIcon && !isCompactLayout ? ' Weiterarbeiten' : null}
      </button>
    );
  };

  /** MEMCORE Modul B: „Session beenden“ — Vorschau-Speichern + Archivieren (Variante A). */
  const renderSessionEndButton = () => {
    if (!memcore || !activeThread) return null;
    const speicherbar = hatSpeicherbarenInhalt(activeThread.messages);
    return (
      <button
        type="button"
        onClick={() => setMemSession({ threadId: activeThread.id, pendingAction: 'archive' })}
        disabled={!speicherbar}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          compactChrome ? 'px-1 py-0 rounded-md text-[10px]' : 'px-2.5 py-1.5 rounded-lg text-xs'
        } ${speicherbar ? PORTAL_BTN.secondary : PORTAL_BTN.secondaryDisabled}`}
        title={
          speicherbar
            ? 'Session beenden — Merkpunkte prüfen und speichern'
            : 'Session beenden (noch kein speicherbarer Inhalt)'
        }
      >
        <BookMarked className={compactChrome ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {!compactChrome && ' Session beenden'}
      </button>
    );
  };

  /** Stream 4: „Fall-Gedächtnis (n)“ — gepinnte Eckdaten ansehen/entfernen. */
  const renderCaseFactsButton = () => {
    if (!memcore) return null;
    const n = activeCaseFacts.length;
    return (
      <button
        type="button"
        onClick={() => setShowCaseFacts(s => !s)}
        disabled={n === 0}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          compactChrome ? 'px-1 py-0 rounded-md text-[10px]' : 'px-2.5 py-1.5 rounded-lg text-xs'
        } ${n > 0 ? PORTAL_BTN.secondary : PORTAL_BTN.secondaryDisabled}`}
        aria-expanded={showCaseFacts}
        title={
          n > 0
            ? 'Fall-Gedächtnis: gemerkte Eckdaten dieses Chats (werden in Entwürfen vorbefüllt)'
            : 'Noch keine Fall-Eckdaten gemerkt — unter einer eigenen Nachricht „Merken“ wählen'
        }
      >
        <Brain className={compactChrome ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {compactChrome ? n : `Fall-Gedächtnis${n > 0 ? ` (${n})` : ''}`}
        {n > 0 ? (showCaseFacts ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />) : null}
      </button>
    );
  };

  const renderArchiveToggle = () =>
    archivedList.length > 0 ? (
      <button
        type="button"
        onClick={() => setShowArchive(s => !s)}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          compactChrome
            ? 'px-1 py-0 rounded-md text-[10px]'
            : 'px-2.5 py-1.5 rounded-lg text-xs ml-auto'
        } ${PORTAL_BTN.secondary}`}
        aria-expanded={showArchive}
        title={`Archiv (${archivedList.length})`}
      >
        <Archive className={compactChrome ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {compactChrome ? archivedList.length : `Archiv (${archivedList.length})`}
        {showArchive ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
      </button>
    ) : null;

  const documentContextSwitchTitle = documentDisplayName?.trim()
    ? `Dokument-Kontext für „${documentDisplayName.trim()}“ umschalten`
    : akteBound
      ? 'Inventar und gewählte Akten-Dokumente an ARKO senden'
      : 'Dokument-Kontext ein- oder ausschalten';

  const renderAkteContextSwitch = (opts?: { size?: 'sm' | 'md'; testId?: boolean }) => {
    if (chatHome) return null;
    if (akten.length === 0) return null;
    const size = opts?.size ?? (compactChrome ? 'sm' : 'md');
    return (
      <PortalSwitch
        checked={akteBound}
        onChange={toggleAkteContext}
        label="Akte-Kontext"
        size={size}
        data-testid={opts?.testId ? 'portal-switch-akte-context' : undefined}
        title={
          akteBound
            ? 'ARKO ist an diese Akte gebunden — unabhängig davon, ob ein einzelnes Dokument in OnlyOffice offen ist'
            : 'Akte-Kontext einschalten — ARKO arbeitet in der letzten oder gewählten Akte'
        }
      />
    );
  };

  const renderDocumentContextSwitch = (opts?: { size?: 'sm' | 'md'; testId?: boolean }) => {
    if (!docAvailable && !akteBound) return null;
    const size = opts?.size ?? (compactChrome ? 'sm' : 'md');
    return (
      <PortalSwitch
        checked={activeDocumentContextEnabled}
        onChange={toggleDocumentContext}
        label="Dokument-Kontext"
        size={size}
        data-testid={opts?.testId ? 'portal-switch-document-context' : undefined}
        title={documentContextSwitchTitle}
      />
    );
  };

  const shortDocName = useMemo(() => {
    const raw = documentDisplayName?.trim();
    if (!raw) return null;
    return raw.length > 28 ? `${raw.slice(0, 26)}…` : raw;
  }, [documentDisplayName]);

  const renderBackgroundJobChips = () => (
    analysisViews.map((job) => (
      <ArkoBackgroundAnalysisChip
        key={job.request_id}
        job={job}
        open={openJobId === job.request_id}
        onOpenChange={(next) => setOpenJobId(next ? job.request_id : null)}
        onCancel={cancelJob}
        onDismiss={dismissFinished}
        onContinue={akteBound ? openAnalysisWeiterarbeiten : undefined}
        onCopy={copyAnalysisText}
        onPin={memcore ? pinFactToActiveThread : undefined}
        onRemember={memcore ? (text) => {
          const body = text.trim();
          if (!body) return;
          setMemThumb({ messageId: `analyse:${job.request_id}`, text: body });
        } : undefined}
      />
    ))
  );

  const renderCompletionNotices = () => (
    completionNotices.length > 0 ? (
      <div className="pointer-events-none absolute inset-x-2 top-2 z-20 flex flex-col gap-1" aria-live="polite">
        {completionNotices.map((notice) => (
          <div
            key={notice.id}
            className={`pointer-events-auto inline-flex items-center gap-2 self-start rounded-full border px-2.5 py-1 text-[10px] font-medium shadow-lg shadow-black/40 ${
              notice.success
                ? 'border-emerald-400/25 bg-emerald-950/90 text-emerald-100'
                : 'border-amber-400/25 bg-amber-950/90 text-amber-100'
            }`}
          >
            {notice.success ? (
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            ) : (
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            )}
            <span>{notice.summary}</span>
            <button
              type="button"
              onClick={() => dismissNotice(notice.id)}
              className="rounded-full p-0.5 opacity-70 hover:opacity-100"
              aria-label="Hinweis schließen"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    ) : null
  );

  const handleAktePickerChange = (rawId: string) => {
    const id = rawId.trim();
    if (!id) {
      onBoundAkteChange?.(null);
      return;
    }
    const row = akten.find((akte) => akte.id === id);
    if (row) onBoundAkteChange?.(row);
  };

  const aktePanelDocuments = availableDocuments ?? [];
  const akteInventoryCount = availableDocuments ? availableDocuments.length : akteDocumentCount;

  const akteStatusLabel = chatHome === 'haupt' ? '' : boundAkte?.title?.trim()
    ? `ARKO arbeitet in Akte ${boundAkte.title.trim()} · ${akteInventoryCount} Dokumente`
    : null;

  const toggleAkteDocsPanel = () => {
    setAkteDocsPanelOpen((prev) => {
      const next = !prev;
      savePortalAkteDocsPanelExpanded(customerId, next);
      return next;
    });
  };

  const submitAkteDocRename = async (documentId: string) => {
    if (!onRenameAkteDocument) return;
    const nextName = akteDocRenameDraft.trim();
    if (!nextName) {
      setWarning('Dateiname fehlt.');
      return;
    }
    setAkteDocRenameBusy(true);
    try {
      await onRenameAkteDocument(documentId, nextName);
      setAkteDocRenameId(null);
      setWarning(null);
    } catch (err) {
      setWarning(err instanceof Error ? err.message : String(err));
    } finally {
      setAkteDocRenameBusy(false);
    }
  };

  const renderAkteDocumentsPanel = () => {
    if (chatHome || freeDocumentColumn) return null;
    if (!akteBound) return null;
    const count = akteInventoryCount;
    const extractDocs = selectedDocumentsForActiveContext.filter((doc) =>
      hasUsableAkteExtract(doc.documentContext),
    );
    const extractTitles = extractDocs.map((doc) => doc.title?.trim() || 'Dokument');
    const extractTexts = extractDocs.map((doc) => String(doc.documentContext || ''));
    const failedCount = selectedDocumentsForActiveContext.filter((doc) =>
      isFailedAkteLazyExtractContext(doc.documentContext),
    ).length;
    const recognizingCount = selectedDocumentsForActiveContext.filter((doc) =>
      isRecognizingAkteLazyExtractContext(doc.documentContext)
      || isPendingAkteLazyExtractContext(doc.documentContext),
    ).length;
    const fuehrenReady = akteFuehrenChatActionsReady({
      akteBound: true,
      selectedWithExtractCount: extractTitles.length,
      extracting: extractingDocumentIds.length > 0 || recognizingCount > 0,
    });
    const runFuehren = (action: (typeof AKTE_FUEHREN_CHAT_ACTIONS)[number]) => {
      if (!fuehrenReady) return;
      const userTexts = (activeThread?.messages ?? [])
        .filter((m) => m.role === 'user')
        .map((m) => String(m.text || ''));
      dispatchAkteFuehrenChat(
        buildAkteFuehrenPrompt(action, extractTitles, extractTexts, extractAkteSchreibenConcern(userTexts)),
      );
    };
    return (
      <div className="flex-shrink-0 border-b border-white/[0.06] bg-slate-950/40">
        <button
          type="button"
          onClick={toggleAkteDocsPanel}
          className="flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-[10px] font-semibold text-slate-300 hover:bg-white/[0.03]"
          aria-expanded={akteDocsPanelOpen}
        >
          <span>Dokumente dieser Akte ({count})</span>
          {akteDocsPanelOpen ? (
            <ChevronUp className="h-3 w-3 text-slate-500" aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3 text-slate-500" aria-hidden />
          )}
        </button>
        {akteDocsPanelOpen ? (
          <>
          <ul
            className="max-h-40 overflow-y-auto custom-scrollbar divide-y divide-white/[0.05] border-t border-white/[0.05]"
            data-akte-lazy-extract={PORTAL_AKTE_LAZY_EXTRACT_MARKER}
            data-akte-followup-context={PORTAL_AKTE_FOLLOWUP_CONTEXT_MARKER}
            data-portal-local-ocr={PORTAL_LOCAL_OCR_MARKER}
          >
            {aktePanelDocuments.length === 0 ? (
              <li className="px-2 py-1.5 text-[10px] text-slate-500">Noch keine Dokumente in dieser Akte.</li>
            ) : (
              aktePanelDocuments.map((doc) => {
                const selected = activeSelectedDocumentIds.includes(doc.documentId);
                const extracting = extractingDocumentIds.includes(doc.documentId);
                const recognizing = selected
                  && !extracting
                  && isRecognizingAkteLazyExtractContext(doc.documentContext);
                const extractFailReason = selected && !recognizing
                  ? akteLazyExtractFailureReason(doc.documentContext)
                  : null;
                const renaming = akteDocRenameId === doc.documentId;
                const canRenameThis = Boolean(onRenameAkteDocument && canWriteAkteDocuments && doc.nc_path?.trim());
                return (
                  <li key={doc.documentId} className="flex items-center gap-1.5 px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelectedDocumentForActiveThread(doc.documentId)}
                      className="h-3.5 w-3.5 flex-shrink-0 rounded border-white/20 bg-slate-900/60 text-violet-500 focus:ring-violet-400/40"
                      aria-label={`„${doc.title}“ für ARKO auswählen`}
                      title="Für ARKO auswählen — Auszug wird geladen, OnlyOffice muss nicht offen sein"
                    />
                    {extracting ? (
                      <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin text-violet-300" aria-hidden />
                    ) : null}
                    {recognizing ? (
                      <span
                        className="flex-shrink-0 rounded-full border border-violet-400/35 bg-violet-500/15 px-1.5 py-0 text-[9px] font-medium text-violet-100"
                        title="Lokale Texterkennung läuft weiter — OnlyOffice-Anzeige ist kein Auszug"
                      >
                        {PORTAL_AKTE_LAZY_EXTRACT_RECOGNIZING_HINT}
                      </span>
                    ) : null}
                    {renaming ? (
                      <input
                        value={akteDocRenameDraft}
                        onChange={(e) => setAkteDocRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void submitAkteDocRename(doc.documentId);
                          if (e.key === 'Escape') setAkteDocRenameId(null);
                        }}
                        autoFocus
                        className="min-w-0 flex-1 rounded border border-white/[0.12] bg-slate-900 px-1.5 py-0.5 text-[10px] text-slate-100"
                        aria-label="Neuer Dateiname"
                      />
                    ) : (
                      <span
                        className="min-w-0 flex-1 truncate text-[11px] text-slate-200"
                        title={
                          extractFailReason
                            ? `Auszug fehlgeschlagen (${extractFailReason})`
                            : doc.title
                        }
                      >
                        {doc.title}
                        {extractFailReason ? (
                          <span className="ml-1 text-[9px] text-amber-300/90">
                            Auszug {extractFailReason}
                          </span>
                        ) : null}
                      </span>
                    )}
                    {renaming ? (
                      <button
                        type="button"
                        onClick={() => void submitAkteDocRename(doc.documentId)}
                        disabled={akteDocRenameBusy}
                        className="text-[10px] text-violet-200 hover:text-white disabled:opacity-50"
                      >
                        Speichern
                      </button>
                    ) : (
                      <>
                        {selected ? (
                          <button
                            type="button"
                            onClick={() => {
                              const next = excludeIrrelevantAkteDoc(activeSelectedDocumentIds, doc.documentId);
                              if (next.length === activeSelectedDocumentIds.length) return;
                              toggleSelectedDocumentForActiveThread(doc.documentId);
                            }}
                            className="flex-shrink-0 text-[9px] text-slate-500 hover:text-amber-200"
                            title="Für ARKO in dieser Akte nicht verwenden"
                            aria-label={`„${doc.title}“ als nicht relevant markieren`}
                          >
                            Nicht relevant
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => onOpenAkteDocument?.(doc.documentId)}
                          disabled={!onOpenAkteDocument}
                          className="inline-flex rounded p-1 text-slate-500 hover:bg-white/[0.06] hover:text-slate-100 disabled:opacity-35"
                          title="In OnlyOffice öffnen"
                          aria-label={`„${doc.title}“ in OnlyOffice öffnen`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (canRenameThis) {
                              setAkteDocRenameId(doc.documentId);
                              setAkteDocRenameDraft(doc.title);
                              return;
                            }
                            onOpenAkteDocument?.(doc.documentId);
                          }}
                          className="inline-flex rounded p-1 text-slate-500 hover:bg-white/[0.06] hover:text-slate-100"
                          title={canRenameThis ? 'Umbenennen' : 'In OnlyOffice öffnen'}
                          aria-label={canRenameThis ? `„${doc.title}“ umbenennen` : `„${doc.title}“ in OnlyOffice öffnen`}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </>
                    )}
                  </li>
                );
              })
            )}
          </ul>
          <div
            className="flex flex-wrap gap-1 border-t border-white/[0.05] px-2 py-1.5"
            data-akte-fuehren={PORTAL_AKTE_FUEHREN_MARKER}
          >
            {AKTE_FUEHREN_CHAT_ACTIONS.map((action) => (
              <button
                key={action}
                type="button"
                disabled={!fuehrenReady}
                onClick={() => runFuehren(action)}
                data-akte-subsumtion={
                  action === 'subsumieren' ? PORTAL_AKTE_SUBSUMTION_MARKER : undefined
                }
                data-akte-schreiben={
                  action === 'schreiben' ? PORTAL_AKTE_SCHREIBEN_MARKER : undefined
                }
                className="rounded-full border border-white/[0.10] bg-slate-900/70 px-2 py-0.5 text-[9px] font-medium text-slate-200 hover:border-violet-400/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                title={akteFuehrenChatActionsHint({
                  ready: fuehrenReady,
                  extracting: extractingDocumentIds.length > 0 || recognizingCount > 0,
                  failedCount,
                  action,
                })}
              >
                {AKTE_FUEHREN_CHAT_LABELS[action]}
              </button>
            ))}
            <p
              className="basis-full px-0.5 pt-0.5 text-[9px] leading-tight text-slate-500"
              data-akte-schreiben-hint={PORTAL_AKTE_SCHREIBEN_MARKER}
            >
              {PORTAL_AKTE_SCHREIBEN_HINT}
            </p>
          </div>
          </>
        ) : null}
      </div>
    );
  };

  const renderStatusBar = () => (
    <div className={`flex-shrink-0 ${PORTAL_CHAT.statusBar}`} aria-live="polite">
      {backgroundAnalysisOpen && analysisViews.length > 0 ? renderBackgroundJobChips() : null}
      {!freeDocumentColumn && !chatHome && akten.length > 0 ? (
        <label className="inline-flex items-center gap-1 min-w-0 max-w-[14rem] text-slate-400">
          <span className="sr-only">Akte für ARKO wählen</span>
          <select
            value={boundAkte?.id ?? ''}
            onChange={(e) => handleAktePickerChange(e.target.value)}
            className="max-w-full truncate rounded border border-white/[0.12] bg-slate-950/80 px-1.5 py-0.5 text-[10px] text-slate-200"
            aria-label="Gebundene Akte"
            title="Akte, in der ARKO arbeitet"
          >
            <option value="">Keine Akte gebunden</option>
            {akten.map((akte) => (
              <option key={akte.id} value={akte.id}>
                {akte.title.trim() || 'Akte'}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {!freeDocumentColumn && akten.length > 0 ? (
        <span className="text-slate-700" aria-hidden>
          ·
        </span>
      ) : null}
      {activeIntentLabel ? (
        <PortalBadge
          label={`Aktiv: ${activeIntentLabel}`}
          accent="blue"
          className="!px-1.5 !py-0 !text-[9px] !font-medium"
        />
      ) : (
        <span className="text-slate-600 italic">Kein Modus gewählt</span>
      )}
      <span className="text-slate-700" aria-hidden>
        ·
      </span>
      {activeContextAvailable ? (
        <span className="truncate min-w-0 text-violet-300/90">
          {!freeDocumentColumn && akteStatusLabel
            ? `${akteStatusLabel}${
                synthesisDocumentLabelsForActiveContext
                  ? ` · ARKO nutzt: ${synthesisDocumentLabelsForActiveContext}`
                  : shortDocName
                    ? ` · Fokus: ${shortDocName}`
                    : ''
              }`
            : synthesisDocumentLabelsForActiveContext
            ? `ARKO nutzt: ${synthesisDocumentLabelsForActiveContext}${shortDocName ? ` · Fokus: ${shortDocName}` : ''}`
            : activeDocumentContextEnabled && shortDocName
              ? `ARKO arbeitet mit: ${shortDocName}`
              : activeSynthesisDocumentLabels
                ? `Kontext pausiert: ${activeSynthesisDocumentLabels}`
                : 'Dokument-Kontext pausiert'}
        </span>
      ) : (
        <span className="text-slate-600">
          {!freeDocumentColumn && akteStatusLabel
            ? akteStatusLabel
            : activeDocumentId?.trim()
              ? 'Dokument-Kontext wird geladen'
              : 'Kein Dokument geöffnet'}
        </span>
      )}
      {!freeDocumentColumn && akteBound && activeThread?.aktenStand ? (
        <>
          <span className="text-slate-700" aria-hidden>·</span>
          <span
            className="truncate min-w-0 max-w-[18rem] text-slate-400"
            title={formatAkteStandParagraph(activeThread.aktenStand) ?? undefined}
          >
            {previewAkteStandLabel(activeThread.aktenStand, akteInventoryCount)}
          </span>
        </>
      ) : null}
      {!freeDocumentColumn && akten.length > 0 ? (
        <>
          <span className="text-slate-700" aria-hidden>·</span>
          {renderAkteContextSwitch({ size: 'sm', testId: true })}
        </>
      ) : null}
      {docAvailable || akteBound ? (
        <>
          <span className="text-slate-700" aria-hidden>·</span>
          {renderDocumentContextSwitch({ size: 'sm', testId: true })}
        </>
      ) : null}
      {shortDocName ? (
        <span className="text-slate-500"> · {shortDocName}</span>
      ) : null}
      {backgroundAnalysisOpen && activeContextAvailable ? (
        <>
          <span className="text-slate-700" aria-hidden>·</span>
          <button
            type="button"
            onClick={startBackgroundDocumentAnalysis}
            disabled={!canEnqueue || !backgroundAnalysisReady}
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-violet-400/20 bg-violet-500/[0.08] px-2 py-0.5 text-[9px] font-semibold text-violet-200 transition-colors hover:bg-violet-500/[0.16] disabled:cursor-not-allowed disabled:opacity-45"
            title={backgroundAnalysisTitle}
          >
            {hasRunningJobs ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="h-3 w-3" aria-hidden />
            )}
            {backgroundAnalysisLabel}
          </button>
        </>
      ) : null}
    </div>
  );

  const renderHauptStatusBar = () => {
    const hasJobs = analysisViews.length > 0;
    const docLine = activeContextAvailable
      ? (synthesisDocumentLabelsForActiveContext
        ? `ARKO nutzt: ${synthesisDocumentLabelsForActiveContext}${shortDocName ? ` · Fokus: ${shortDocName}` : ''}`
        : activeDocumentContextEnabled && shortDocName
          ? `ARKO arbeitet mit: ${shortDocName}`
          : activeSynthesisDocumentLabels
            ? `Kontext pausiert: ${activeSynthesisDocumentLabels}`
            : '')
      : '';
    if (!hasJobs && !docLine) return null;
    return (
      <div
        className="flex flex-shrink-0 flex-wrap items-center gap-1.5 border-b border-violet-400/30 bg-[#07070a] px-2 py-1.5"
        data-testid="portal-haupt-arko-status"
      >
        {backgroundAnalysisOpen && hasJobs ? renderBackgroundJobChips() : null}
        {docLine ? <span className="min-w-0 truncate text-[10px] text-violet-200/80">{docLine}</span> : null}
        {backgroundAnalysisOpen && activeContextAvailable ? (
          <button
            type="button"
            onClick={startBackgroundDocumentAnalysis}
            disabled={!canEnqueue || !backgroundAnalysisReady}
            className="inline-flex items-center gap-1 rounded-md border border-violet-400/40 bg-violet-600/30 px-2 py-1 text-[10px] font-semibold text-violet-100 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45"
            title={backgroundAnalysisTitle}
          >
            {hasRunningJobs ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Sparkles className="h-3 w-3" aria-hidden />}
            {backgroundAnalysisLabel}
          </button>
        ) : null}
      </div>
    );
  };

  const chatArea = (
    <div className="relative flex-1 min-h-0 flex flex-col">
      {renderCompletionNotices()}
      {chatHome === 'akte' && scopedActiveTabs.length === 0 ? (
        <div
          className="flex flex-1 flex-col items-start justify-center gap-3 px-4 py-6"
          data-testid="portal-akte-arko-empty"
        >
          <p className="m-0 text-sm text-slate-300">Noch kein Chat in dieser Akte.</p>
          <button
            type="button"
            data-testid="portal-akte-arko-new"
            onClick={newChat}
            disabled={fullActive}
            className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45"
          >
            Chat dieser Akte anlegen
          </button>
        </div>
      ) : activeThread && (!chatHome || scopedActiveTabs.some((thread) => thread.id === activeThread.id)) ? (
        <InlineChat
          key={activeThread.id}
          webhookUrl={webhookUrl}
          title={title}
          subtitle={subtitle}
          initialMessage={initialBotMessage}
          accentColor={isCursorLayout || isCompactLayout ? 'cursor' : accentColor}
          mode={mode}
          judicatureOpen={judicatureOpen}
          extraBody={{
            ...(extraBody ?? {}),
            threadId: activeThread.id,
            ...(chatHome === 'haupt'
              ? {}
              : boundAkte?.id
                ? {
                    akteId: boundAkte.id,
                    akteTitle: chatHome === 'akte' ? undefined : boundAkte.title,
                  }
                : {}),
            ...(activeThread.akteHandoff?.trim()
              ? { akteHandoff: capAkteHandoff(activeThread.akteHandoff) }
              : {}),
          }}
          documentContext={documentContextForPrompt}
          documentContextEnabled={activeDocumentContextEnabled}
          documentDisplayName={documentDisplayName}
          activeDocumentId={activeDocumentId}
          openDocumentsMeta={activeDocumentContextEnabled ? openDocumentsMeta : undefined}
          selectedDocuments={selectedDocumentsForActiveContext}
          synthesisDocumentLabels={synthesisDocumentLabelsForActiveContext}
          feedbackAkteBound={Boolean(akteNcPath || boundAkte?.id)}
          documentExtractFingerprint={documentExtractFingerprint}
          documentAnalyzeDone={documentAnalyzeDone}
          onAnalyzeDocumentsReady={onAnalyzeDocumentsReady}
          timeoutMs={timeoutMs}
          showQuickActions={showQuickActions}
          showRechtsfrageQuickAction={showRechtsfrageQuickAction}
          usePortalChips={showQuickActions}
          chipAccent={isCursorLayout || isCompactLayout ? 'cursor' : accentColor === 'purple' ? 'violet' : 'blue'}
          hideHeader={hideChatHeader || isCursorLayout}
          compactChrome={isCompactLayout}
          shellClassName={chatShellClassName}
          chatVariant={isCursorLayout || isCompactLayout ? 'cursor' : 'default'}
          cursorCustomerName={cursorCustomerName}
          cursorKanzlei={cursorKanzlei}
          messages={activeThread.messages}
          onMessagesChange={activeMessagesSetter}
          enableContractIntent={enableContractIntent}
          onContractGenerate={onContractGenerate}
          onExportSchreibenPdf={onExportSchreibenPdf}
          onExportSchreibenDocx={onExportSchreibenDocx}
          akteBound={akteBound}
          akteHandoff={activeThread.akteHandoff}
          onContinueAkte={akteBound ? openAkteHandoff : undefined}
          onOpenPortalDocument={onOpenPortalDocument}
          onApplySynthesisToDocument={onApplySynthesisToDocument}
          synthesisHasOpenDocument={synthesisHasOpenDocument}
          onActiveIntentChange={handleActiveIntentChange}
          hubModeId={activeThread.hubModeId ?? null}
          onHubModeCommit={commitHubMode}
          customerId={customerId}
          enableMemorySave={Boolean(memcore)}
          onMemorySaveRequest={memcore ? setMemThumb : undefined}
          memcoreRecall={memcore ?? null}
          pinnedMemContext={activePinnedMemContext}
          onPinFact={memcore ? pinFactToActiveThread : undefined}
          authToken={authToken}
          guidedFlowState={activeThread.guidedFlow ?? null}
          onGuidedFlowStateChange={handleGuidedFlowStateChange}
          paragraphIndexMap={paragraphIndexMap}
          cursorSelection={cursorSelection}
          onMarkDocumentAnalyzed={onMarkDocumentAnalyzed}
          appearance={chatHome === 'haupt' ? 'haupt' : undefined}
        />
      ) : null}
    </div>
  );

  const mainColumn = (
    <div className={`flex flex-col h-full min-h-0 flex-1 ${isCompactLayout ? 'gap-0' : 'gap-2'}`}>
      {isCompactLayout ? (
        <div className="flex-shrink-0 border-b border-white/[0.06] px-0.5 py-1 @container min-w-0">
          {renderThreadColumns('default')}
          <div className="flex items-center gap-1 min-w-0 mt-1 overflow-x-auto custom-scrollbar">
            {!freeDocumentColumn ? renderContinueAkteButton() : null}
            {renderSessionEndButton()}
            {renderCaseFactsButton()}
            {renderArchiveToggle()}
          </div>
        </div>
      ) : null}

      {(isCompactLayout || (showQuickActions && !isCursorLayout)) && (
        <>
          {renderStatusBar()}
          {renderAkteDocumentsPanel()}
        </>
      )}

      {!isCompactLayout && !isCursorLayout ? (
        <>
          <div className="@container min-w-0 flex-shrink-0">
            {renderThreadColumns('default')}
            <div className="flex items-center gap-1.5 mt-1 min-w-0 overflow-x-auto custom-scrollbar">
              {renderContinueAkteButton()}
              {renderSessionEndButton()}
              {renderCaseFactsButton()}
              {renderArchiveToggle()}
            </div>
          </div>

          {(akten.length > 0 || docAvailable || akteBound) && (
            <div className={`flex-shrink-0 flex items-center flex-wrap gap-3 px-2 py-1 ${PORTAL_CHAT.contextBar}`}>
              {renderAkteContextSwitch({ size: 'md' })}
              {renderDocumentContextSwitch({ size: 'md' })}
            </div>
          )}
          {!docAvailable && !akteBound && akten.length === 0 && (
            <div className={`flex-shrink-0 flex items-center px-2 py-1 ${PORTAL_CHAT.contextBar}`}>
              <span className="text-[10px] text-slate-500 italic">Kein Dokument geöffnet</span>
            </div>
          )}
        </>
      ) : null}

      {isCursorLayout && showQuickActions ? (
        <>
          {chatHome === 'akte' ? renderAkteStatusBar() : chatHome === 'haupt' ? renderHauptStatusBar() : renderStatusBar()}
          {renderAkteDocumentsPanel()}
        </>
      ) : null}

      {warning && (
        <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center justify-between flex-shrink-0">
          <span>{warning}</span>
          <button
            onClick={() => setWarning(null)}
            className="text-amber-300/70 hover:text-amber-200 ml-3"
            aria-label="Hinweis schliessen"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {memcore && showCaseFacts && activeCaseFacts.length > 0 && (
        <div className={`flex-shrink-0 max-h-44 overflow-y-auto p-2 space-y-1 custom-scrollbar ${
          chatHome === 'haupt' ? 'border-b border-violet-400/30 bg-[#07070a]' : PORTAL_CHAT.archivePanel
        }`}>
          <div className="px-2 pb-1 text-[10px] text-slate-500">
            Fall-Eckdaten dieses Chats — ARKO bezieht sie automatisch in Entwürfe ein. Bleiben lokal in diesem Browser.
          </div>
          {activeCaseFacts.map(f => (
            <div key={f.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/60">
              <Brain className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${chatHome === 'haupt' ? 'text-violet-300/80' : 'text-blue-300/70'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 whitespace-pre-wrap break-words">{f.inhalt}</div>
                <div className="text-[10px] text-slate-500">
                  Gemerkt {new Date(f.ts).toLocaleString('de-AT')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeCaseFact(f.id)}
                className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10 transition-colors flex-shrink-0"
                title="Aus dem Fall-Gedächtnis entfernen"
                aria-label="Fall-Eckdaten entfernen"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showArchive && archivedList.length > 0 && (
        <div className={`flex-shrink-0 max-h-44 overflow-y-auto p-2 space-y-1 custom-scrollbar ${
          chatHome === 'haupt' ? 'border-b border-violet-400/30 bg-[#07070a]' : PORTAL_CHAT.archivePanel
        }`}>
          {archivedList.map(a => (
            <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/60">
              <Archive className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 truncate">{a.title}</div>
                <div className="text-[10px] text-slate-500">
                  {new Date(a.archivedAt).toLocaleString('de-AT')} · {a.messages.length} Nachrichten
                </div>
              </div>
              <button
                type="button"
                onClick={() => restoreFromArchive(a.id)}
                disabled={fullActive}
                className={`p-1.5 rounded transition-colors ${
                  fullActive
                    ? 'text-slate-600 cursor-not-allowed'
                    : 'text-violet-300 hover:bg-violet-500/10'
                }`}
                title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Wiederherstellen'}
                aria-label="Aus Archiv wiederherstellen"
              >
                <ArchiveRestore className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => deleteArchived(a.id)}
                className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10 transition-colors"
                title="Endgültig löschen"
                aria-label="Archivierten Chat endgültig löschen"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {chatArea}
    </div>
  );

  return (
    <>
      {isCursorLayout ? (
        <div className={`arko-cursor-workspace flex flex-col flex-1 min-h-0 h-full w-full bg-[#07070a] ${arkoLightClass}`}>
          {chatHome === 'akte' ? renderAkteCursorBar() : renderCursorTopBar()}
          <div className="flex flex-col flex-1 min-h-0">{mainColumn}</div>
        </div>
      ) : (
        mainColumn
      )}

      {/* ── MEMCORE Modul B: Dialoge (Nudge, Session beenden, Daumen 2-Schritt) ── */}
      {memcore && memNudge && (() => {
        const t = state.threads[memNudge.threadId];
        if (!t) return null;
        return (
          <MemoryNudgeDialog
            threadTitle={t.title}
            onSave={() => {
              setMemSession({ threadId: memNudge.threadId, pendingAction: memNudge.action });
              setMemNudge(null);
            }}
            onDecline={() => {
              markMemNudgeDone(memNudge.threadId);
              const { threadId, action } = memNudge;
              setMemNudge(null);
              if (action === 'archive') archiveThreadNow(threadId);
              else deleteThreadNow(threadId);
            }}
            onCancel={() => setMemNudge(null)}
          />
        );
      })()}

      {memcore && memSession && (() => {
        const t = state.threads[memSession.threadId];
        if (!t) return null;
        return (
          <MemorySessionDialog
            ctx={{ kanzleiId: memcore.kanzleiId, userId: memcore.userId }}
            curatorUrl={memcore.curatorUrl}
            threadId={t.id}
            threadTitle={t.title}
            transcript={buildChatTranscript(t.messages)}
            onClose={() => setMemSession(null)}
            onFinished={() => {
              markMemNudgeDone(memSession.threadId);
              const { threadId, pendingAction } = memSession;
              setMemSession(null);
              if (pendingAction === 'delete') deleteThreadNow(threadId);
              else archiveThreadNow(threadId);
            }}
          />
        );
      })()}

      {akteHandoffOpen && activeThread ? (
        <AkteHandoffDialog
          ctx={memcore ? { kanzleiId: memcore.kanzleiId, userId: memcore.userId } : null}
          curatorUrl={memcore?.curatorUrl}
          threadId={activeThread.id}
          threadTitle={activeThread.title}
          transcript={buildChatTranscript(activeThread.messages)}
          localDraft={analysisHandoffSeed
            ? buildLocalAkteHandoff({ lastBotText: analysisHandoffSeed })
            : buildLocalAkteHandoff({
              lastUserText: lastUserTextForStand(activeThread.messages),
              lastBotText: lastBotTextForStand(activeThread.messages),
              selectedDocTitles: activeThread.aktenStand?.selectedDocTitles
                ?? selectedTitlesForStand(activeThread, availableDocumentsById),
            })}
          onClose={() => {
            setAkteHandoffOpen(false);
            setAnalysisHandoffSeed(null);
          }}
          onContinue={continueAkteFromHandoff}
        />
      ) : null}

      {memcore && memThumb && activeThread && (
        <MemoryThumbDialog
          ctx={{ kanzleiId: memcore.kanzleiId, userId: memcore.userId }}
          curatorUrl={memcore.curatorUrl}
          quelleRef={`portal:${customerId}:${activeThread.id}:${memThumb.messageId}`}
          nachricht={memThumb.text}
          onClose={() => setMemThumb(null)}
        />
      )}
    </>
  );
};

export default PortalArkoChatWorkspace;
